import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import SolutionTypeFlowDiagram, { buildSvgString, svgToPngDataUrl } from "../components/SolutionTypeFlowDiagram";
import {
  Workflow,
  Search,
  Filter,
  Loader2,
  X,
  Layers,
  Building2,
  Camera,
  MessageCircle,
  Cpu,
  RotateCcw,
  DollarSign,
  Clock,
  ChevronDown,
  ChevronRight,
  Grid3x3,
  BookOpen,
  Save,
  FileText,
  Download,
  GitBranch,
  HeartPulse,
  Info,
  Users,
  Network,
  Router,
  Cable,
  Cloud,
  Shield,
  Plus,
  Package,
  Briefcase,
  Sparkles,
  ChevronsRight,
  Copy,
  MoreVertical,
  Settings2,
  CheckCircle2,
  CircleDashed,
  ClipboardList,
  Archive,
  Edit,
  Move,
  RefreshCw,
  Star,
  Unlink,
  HelpCircle,
} from "lucide-react";
import PageForm from "../components/PageForm";
import WizardPopover from "../components/WizardPopover";
import FloatingPanel from "../components/ui/FloatingPanel";   // v101.98.0 · menús fuera de la tabla
import {
  SolutionRulesFieldsBody,
  AppliesToClientTypes,
  ClientNotificationField,
  OperationModeFields,
  OperationalTogglesGrid,
  OdooActivityMapField,
  SLAFields,
  RulesSummary,
  ProvisioningFlowField,
} from "../components/SolutionRulesFields";
import RecipeMaterialsModal from "../components/RecipeMaterialsModal";
import FiscFormLinkModal from "../components/FiscFormLinkModal";

// ─────────────────────────────────────────────────────────────────────
// NOMENCLATURA CORPORATIVA (Feb-2026, requerimiento Edwin Morales / ALFANET)
//   Antes:  Tecnología > Categoría Principal > Tipo Solución > Hija
//   Ahora:  Línea de Negocio > Servicio > Actividad Específica
//   La "tecnología" queda como parámetro dentro del servicio (viene de Odoo).
// ─────────────────────────────────────────────────────────────────────
const TAX = {
  linea:     "Línea de Negocio",
  servicio:  "Servicio",
  actividad: "Actividad Específica",
};

const NOTIF_LABEL = {
  always: "Siempre",
  servicio_corp_only: "Solo Servicio/Corp",
  none: "No notificar",
};

const MATERIAL_LABEL = {
  maximo: "Máximo obligatorio",
  minimo: "Mínimo obligatorio",
  planificado: "Planificado",
  gratuito: "Gratuito",
  no_aplica: "No aplica",
};

const MATERIAL_COLOR = {
  maximo: "bg-red-100 text-red-800",
  minimo: "bg-amber-100 text-amber-800",
  planificado: "bg-blue-100 text-blue-800",
  gratuito: "bg-green-100 text-green-800",
  no_aplica: "bg-gray-100 text-gray-700",
};

const SCHED_LABEL = { manual: "Manual", automatic: "Automático" };

// Iconos por línea de negocio (mapeo del backend `style.icon`)
const LOB_ICONS = {
  Users, Building2, Cable, Router, Network, Cloud, Shield, Plus, Briefcase, Package,
};

export default function TiposSolucion() {
  const { hasPerm } = useAuth();
  const [view, setView] = useState("hierarchy"); // 'hierarchy' | 'table' | 'flow'
  const [items, setItems] = useState([]);
  const [hierarchy, setHierarchy] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [lobFilter, setLobFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [editingFull, setEditingFull] = useState(false);
  const editAnchorRef = useRef(null);
  const [flowItem, setFlowItem] = useState(null);
  const [recipeItem, setRecipeItem] = useState(null); // v3.0.0 · Receta de Materiales

  const openEditor = (activity, anchorEl) => {
    editAnchorRef.current = anchorEl || null;
    setEditingFull(false);
    setEditing(activity);
  };
  const closeEditor = () => {
    setEditing(null);
    setEditingFull(false);
    editAnchorRef.current = null;
  };

  const canEdit = hasPerm("M7.solution_types.edit");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (lobFilter) params.set("line_of_business", lobFilter);
      if (clientFilter) params.set("client_type", clientFilter);
      const [it, hi, cat, st] = await Promise.all([
        api.get(`/v1/solution-types?${params.toString()}`),
        api.get("/v1/solution-types/hierarchy"),
        api.get("/v1/solution-types/catalog"),
        api.get("/v1/solution-types/stats"),
      ]);
      setItems(it.data.items || []);
      setHierarchy(hi.data);
      setCatalog(cat.data);
      setStats(st.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar tipos de solución");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  return (
    <Layout>
      <div className="space-y-6" data-testid="solution-types-page">
        {/* Header corporativo */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2" style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C4CA12" }} />
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6B7000" }}>Motor de Decisión · Feb 2026</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3" style={{ color: "#0A3747" }}>
              <Workflow className="text-[#0A3747]" /> Tipos de Solución
              <a href="/centro-documentacion#tipos-solucion" title="Manual de Tipos de Solución" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0A3747]/10 hover:bg-[#0A3747]/20 transition" data-testid="help-tipos-header">
                <HelpCircle size={15} className="text-[#0A3747]" />
              </a>
            </h1>
            <p className="mt-1 text-sm max-w-3xl" style={{ color: "#64748B" }}>
              Catálogo corporativo organizado en jerarquía{" "}
              <span className="font-semibold" style={{ color: "#0A3747" }}>Línea de Negocio › Servicio › Actividad Específica</span>.
              Cada actividad define sus propias reglas (SLA, agendamiento, materiales, IA+GPS, notificaciones).
            </p>
          </div>
          <div className="neu-pill-group flex-wrap">
            <ViewButton active={view === "hierarchy"} onClick={() => setView("hierarchy")} icon={Briefcase} label={<span>Jerarquía <span className="text-[9px] font-mono ml-0.5 px-1 rounded" style={{ background: "#E0E621", color: "#0A3747" }}>229</span></span>} testid="view-hierarchy-btn" />
            <ViewButton active={view === "table"}     onClick={() => setView("table")}     icon={Layers}    label="Tabla"    testid="view-table-btn" />
            <ViewButton active={view === "flow"}      onClick={() => setView("flow")}      icon={GitBranch} label="Flujos"  testid="view-flow-btn" />
            {canEdit && <AutoLinkOdooButton onDone={fetchAll} />}
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label={`${TAX.actividad}s activas`} value={stats.total_types} icon={BookOpen} tone="navy" testid="stat-total" />
            <StatCard label={`${TAX.linea}s`}            value={hierarchy?.totals?.lines_of_business || 0} icon={Briefcase} tone="yellow" testid="stat-lob" />
            <StatCard label="Con IA + GPS"                value={stats.with_ai_gps} icon={Cpu} tone="cyan" testid="stat-ia" />
            <StatCard label="Agendamiento automático"     value={stats.auto_scheduling} icon={Clock} tone="green" testid="stat-auto" />
          </div>
        )}

        {/* Filtros (para vistas que los soportan) */}
        {(view === "hierarchy" || view === "table") && (
          <div className="flex flex-wrap gap-3 items-end">
            <div className="relative max-w-md flex-1 min-w-[220px]">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
              <input
                type="text" value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchAll()}
                placeholder="Buscar por código, servicio, actividad, categoría o producto..."
                className="neu-input pl-11"
                data-testid="soltype-search"
              />
            </div>
            {catalog?.lines_of_business && (
              <div className="relative min-w-[240px]">
                <Briefcase size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
                <select
                  value={lobFilter} onChange={(e) => setLobFilter(e.target.value)}
                  className="neu-input pl-10"
                  data-testid="lob-filter"
                >
                  <option value="">Todas las {TAX.linea.toLowerCase()}s</option>
                  {catalog.lines_of_business.map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>
            )}
            {catalog?.client_types && (
              <div className="relative min-w-[200px]">
                <Filter size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
                <select
                  value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                  className="neu-input pl-10"
                  data-testid="client-filter"
                >
                  <option value="">Todos los tipos de cliente</option>
                  {catalog.client_types.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
            )}
            <button
              onClick={fetchAll}
              className="neu-btn neu-btn-accent"
              data-testid="apply-filters"
            >
              Aplicar
            </button>
          </div>
        )}

        {/* Vistas */}
        {view === "hierarchy" && (
          <ExcelTreeView filterText={q} />
        )}
        {view === "excel" && (
          <ExcelTreeView filterText={q} />
        )}
        {view === "table" && (
          <TableView items={items} loading={loading} canEdit={canEdit} onEdit={openEditor} onViewFlow={setFlowItem} onOpenRecipe={setRecipeItem} catalog={catalog} />
        )}
        {view === "flow" && (
          <FlowGalleryView items={items} loading={loading} onViewFlow={setFlowItem} catalog={catalog} />
        )}
      </div>

      {editing && editingFull && (
        <EditModal
          item={editing}
          catalog={catalog}
          onClose={closeEditor}
          onOpenRecipe={() => setRecipeItem(editing)}
          onSaved={(updatedItem) => {
            if (updatedItem && flowItem && flowItem.id === updatedItem.id) {
              setFlowItem(updatedItem);
            }
            closeEditor();
            fetchAll();
          }}
        />
      )}

      {editing && !editingFull && (
        <RulesWizard
          item={editing}
          catalog={catalog}
          anchorRef={editAnchorRef}
          onClose={closeEditor}
          onOpenFull={() => setEditingFull(true)}
          onSaved={(updatedItem) => {
            if (updatedItem && flowItem && flowItem.id === updatedItem.id) {
              setFlowItem(updatedItem);
            }
            closeEditor();
            fetchAll();
          }}
        />
      )}

      {flowItem && (
        <FlowModal
          item={flowItem}
          onClose={() => setFlowItem(null)}
          onEdit={canEdit ? () => { openEditor(flowItem, null); setEditingFull(true); } : null}
        />
      )}

      {recipeItem && (
        <RecipeMaterialsModal
          solutionType={recipeItem}
          onClose={() => { setRecipeItem(null); fetchAll(); }}
        />
      )}
    </Layout>
  );
}


/** v4.2.1 · Helper: nombre del ancestro más cercano (el padre inmediato). */
function getParentName(item) {
  if (!item || !Array.isArray(item.path) || item.path.length < 2) return null;
  return item.path[item.path.length - 2] || null;
}

function ViewButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`neu-pill ${active ? "active" : ""}`}
    >
      <Icon size={13} /> {label}
    </button>
  );
}

function AutoLinkOdooButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Ejecutar auto-vínculo de Tipos de Solución con activity_type de Odoo?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/solution-types/auto-link-odoo");
      toast.success(`Vinculados: ${data.matched_count} · Sin match: ${data.unmatched_count}`);
      onDone?.();
    } catch (e) { toast.error("Error en auto-link"); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} className="neu-pill" style={{ background: "#0A3747", color: "#E9EE6E" }} data-testid="auto-link-odoo">
      {busy ? <Loader2 size={13} className="animate-spin"/> : <Sparkles size={13} />} Auto-vincular Odoo
    </button>
  );
}

function StatCard({ label, value, icon: Icon, tone, testid }) {
  const styles = {
    navy:   { bg: "linear-gradient(145deg, #F1F5F9, #E4EAF0)", fg: "#0A3747", ring: "rgba(163,177,198,0.55)" },
    yellow: { bg: "linear-gradient(145deg, #EDF34C, #C4CA12)", fg: "#3F2E00", ring: "rgba(163,168,33,0.5)" },
    green:  { bg: "linear-gradient(145deg, #34D399, #10B981)", fg: "#064E3B", ring: "rgba(16,185,129,0.5)" },
    cyan:   { bg: "linear-gradient(145deg, #93C5FD, #3B82F6)", fg: "#1E3A8A", ring: "rgba(59,130,246,0.5)" },
    amber:  { bg: "linear-gradient(145deg, #FCD34D, #F59E0B)", fg: "#78350F", ring: "rgba(245,158,11,0.5)" },
  };
  const st = styles[tone] || styles.navy;
  return (
    <div
      className="afc-appear rounded-2xl px-4 py-3 flex items-center justify-between transition-all hover:-translate-y-0.5"
      style={{
        background: st.bg,
        boxShadow: `-6px -6px 14px rgba(255,255,255,0.75), 6px 6px 14px ${st.ring}, inset -1px -1px 3px rgba(0,0,0,0.05), inset 1px 1px 3px rgba(255,255,255,0.5)`,
      }}
      data-testid={testid}
    >
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: st.fg, opacity: 0.75 }}>{label}</div>
        <div className="text-2xl font-bold mt-1" style={{ color: st.fg, textShadow: "0 1px 0 rgba(255,255,255,0.4)" }}>{value}</div>
      </div>
      <div
        className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{
          background: "rgba(255,255,255,0.5)",
          boxShadow: "inset -1.5px -1.5px 4px rgba(255,255,255,0.85), inset 1.5px 1.5px 4px rgba(0,0,0,0.08)",
        }}
      >
        <Icon size={22} style={{ color: st.fg }} strokeWidth={2} />
      </div>
    </div>
  );
}

function FlagIcon({ active, icon: Icon, label }) {
  return (
    <div
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-[#0A3747] text-[#E0E621]" : "bg-gray-100 text-gray-400"}`}
      title={label}
    >
      <Icon size={11} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VISTA JERÁRQUICA CORPORATIVA (nueva - default)
// Línea de Negocio > Servicio > Actividad Específica
// ═══════════════════════════════════════════════════════════════════
function HierarchyView({ hierarchy, loading, canEdit, onEdit, onViewFlow, catalog, filterText }) {
  const [expandedLobs, setExpandedLobs] = useState({});
  const [expandedSvcs, setExpandedSvcs] = useState({});

  // Al primer render, expandir la primera línea automáticamente
  useEffect(() => {
    if (hierarchy?.lines?.length && Object.keys(expandedLobs).length === 0) {
      const initial = {};
      hierarchy.lines.forEach((l, i) => { initial[l.line_of_business] = i === 0; });
      setExpandedLobs(initial);
    }
    // eslint-disable-next-line
  }, [hierarchy?.lines?.length]);

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>;
  }
  if (!hierarchy || !hierarchy.lines?.length) {
    return (
      <div className="py-20 text-center text-sm text-gray-500" data-testid="empty-hierarchy">
        Sin líneas de negocio configuradas.
      </div>
    );
  }

  const filterMatch = (item) => {
    if (!filterText) return true;
    const t = filterText.toLowerCase();
    return (
      item.code?.toLowerCase().includes(t) ||
      item.name?.toLowerCase().includes(t) ||
      item.activity_name?.toLowerCase().includes(t) ||
      item.service_name?.toLowerCase().includes(t)
    );
  };

  return (
    <div className="space-y-4" data-testid="hierarchy-view">
      {/* Leyenda taxonomía corporativa */}
      <div className="bg-gradient-to-r from-[#0A3747] to-[#135166] text-white rounded-lg p-4 flex items-start gap-4 flex-wrap">
        <Sparkles className="text-[#E0E621] flex-shrink-0 mt-1" size={22} />
        <div className="flex-1 min-w-[280px]">
          <div className="text-[10px] uppercase tracking-widest font-mono opacity-70 mb-1">Nueva taxonomía corporativa · Feb 2026</div>
          <div className="text-sm">
            El catálogo se organiza en tres niveles jerárquicos.{" "}
            <span className="font-semibold text-[#E0E621]">La tecnología</span>{" "}
            (Fibra óptica, Radio-enlace, etc.) es un parámetro dentro de cada actividad y viene desde la suscripción del cliente en Odoo.
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono">
          <TaxChip label={TAX.linea}     color="#E0E621" />
          <ChevronsRight size={12} className="opacity-60" />
          <TaxChip label={TAX.servicio}  color="#9BB300" />
          <ChevronsRight size={12} className="opacity-60" />
          <TaxChip label={TAX.actividad} color="#FFFFFF" text="#0A3747" />
        </div>
      </div>

      {/* Líneas de negocio como acordeones corporativos */}
      {hierarchy.lines.map((lob) => {
        const isOpen = expandedLobs[lob.line_of_business];
        const IconComp = LOB_ICONS[lob.style?.icon] || Briefcase;
        const bg = lob.style?.color || "#0A3747";
        const accent = lob.style?.accent || "#E0E621";

        // Contar matches del filtro
        const matches = lob.services.reduce(
          (n, s) => n + s.activities.filter(filterMatch).length,
          0
        );
        if (filterText && matches === 0) return null;

        return (
          <div
            key={lob.line_of_business}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm"
            data-testid={`lob-card-${lob.line_of_business}`}
          >
            {/* Cabecera de Línea de Negocio */}
            <button
              onClick={() => setExpandedLobs({ ...expandedLobs, [lob.line_of_business]: !isOpen })}
              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:brightness-95 transition"
              style={{ background: bg, color: "#fff" }}
              data-testid={`lob-toggle-${lob.line_of_business}`}
            >
              <div
                className="w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ background: accent, color: bg }}
              >
                <IconComp size={22} strokeWidth={2.25} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono opacity-80">{TAX.linea}</div>
                <div className="text-lg font-bold truncate" style={{ fontFamily: "Chivo" }}>{lob.label}</div>
                <div className="text-[11px] opacity-80 mt-0.5">
                  {lob.total_services} {TAX.servicio.toLowerCase()}{lob.total_services !== 1 ? "s" : ""} · {lob.total_activities} {TAX.actividad.toLowerCase()}{lob.total_activities !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="text-right">
                <div
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-mono font-bold"
                  style={{ background: accent, color: bg }}
                >
                  {lob.total_activities}
                </div>
              </div>
              {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>

            {/* Servicios dentro de la línea */}
            {isOpen && (
              <div className="divide-y-2 divide-gray-100 bg-gradient-to-b from-gray-50 to-white">
                {lob.services.map((svc, svcIdx) => {
                  const svcKey = `${lob.line_of_business}::${svc.service_name}`;
                  const svcOpen = expandedSvcs[svcKey] !== false; // default abierto
                  const filtered = svc.activities.filter(filterMatch);
                  if (filterText && filtered.length === 0) return null;
                  return (
                    <div key={svcKey} data-testid={`svc-block-${svcKey}`} className="bg-white">
                      {/* Cabecera de SERVICIO — más prominente y ordenada */}
                      <button
                        onClick={() => setExpandedSvcs({ ...expandedSvcs, [svcKey]: !svcOpen })}
                        className="w-full flex items-center gap-4 px-6 py-3.5 text-left hover:bg-gray-50 transition group border-l-[6px]"
                        style={{ borderColor: accent }}
                        data-testid={`svc-toggle-${svcKey}`}
                      >
                        {/* Numeración del servicio dentro de la línea */}
                        <div
                          className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 font-mono font-bold text-xs"
                          style={{ background: `${accent}40`, color: bg, border: `1.5px solid ${accent}` }}
                          title={`Servicio ${svcIdx + 1}`}
                        >
                          {String(svcIdx + 1).padStart(2, "0")}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div
                            className="text-[9px] font-mono uppercase tracking-[0.22em] font-bold mb-0.5"
                            style={{ color: bg, opacity: 0.7 }}
                          >
                            {TAX.servicio}
                          </div>
                          <div className="text-base font-bold text-gray-900 leading-tight" style={{ fontFamily: "Chivo" }}>
                            {svc.service_name}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-mono font-bold px-2.5 py-1 rounded-full"
                            style={{ background: bg, color: accent }}
                            title="Actividades específicas"
                          >
                            {filtered.length}
                            {filtered.length !== svc.activities.length && (
                              <span className="opacity-70">/{svc.activities.length}</span>
                            )}
                          </span>
                          {svcOpen ? (
                            <ChevronDown size={18} className="text-gray-500 group-hover:text-gray-800 transition" />
                          ) : (
                            <ChevronRight size={18} className="text-gray-500 group-hover:text-gray-800 transition" />
                          )}
                        </div>
                      </button>
                      {svcOpen && (
                        <div className="px-6 pb-4 pt-1 space-y-2 border-l-[6px]" style={{ borderColor: `${accent}40` }}>
                          {filtered.map((act, actIdx) => (
                            <div key={act.id} className="flex items-start gap-2">
                              <div
                                className="mt-3.5 w-4 h-px flex-shrink-0"
                                style={{ background: `${accent}70` }}
                              />
                              <div className="flex-1 min-w-0">
                                <ActivityCard
                                  activity={act}
                                  accent={accent}
                                  bg={bg}
                                  canEdit={canEdit}
                                  onEdit={(anchorEl) => onEdit(act, anchorEl)}
                                  onViewFlow={() => onViewFlow(act)}
                                  clientTypes={[...(catalog?.client_types || []), ...(catalog?.legacy_client_types || [])]}
                                  index={actIdx + 1}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TaxChip({ label, color, text = "#0A3747" }) {
  return (
    <span
      className="px-2 py-0.5 rounded font-bold uppercase tracking-wider text-[10px]"
      style={{ background: color, color: text }}
    >
      {label}
    </span>
  );
}

function ActivityCard({ activity, accent, bg, canEdit, onEdit, onViewFlow, clientTypes, index }) {
  const clientMap = {};
  clientTypes.forEach((c) => { clientMap[c.code] = c.label; });
  const [showFormLink, setShowFormLink] = useState(false);   // v3.9.4 bis

  return (
    <div
      className="border border-gray-200 rounded-md p-3 bg-white hover:border-[#0A3747] hover:shadow transition"
      data-testid={`activity-${activity.code}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {index && (
              <span
                className="font-mono text-[9px] px-1.5 py-0.5 rounded uppercase font-bold flex-shrink-0"
                style={{ background: `${bg}15`, color: bg, border: `1px solid ${accent}70` }}
                title={`Actividad ${index}`}
              >
                #{String(index).padStart(2, "0")}
              </span>
            )}
            <span className="font-mono text-[10px] text-gray-500">{activity.code}</span>
            {activity.technology && (
              <span
                className="font-mono text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider"
                style={{ background: `${accent}33`, color: bg }}
                title="Tecnología (parámetro)"
              >
                {activity.technology.slice(0, 20)}
              </span>
            )}
            {activity.responsible && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono">
                👤 {activity.responsible}
              </span>
            )}
          </div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-gray-400 mb-0.5">
            {TAX.actividad}
          </div>
          <div className="text-sm font-semibold text-gray-900">
            {activity.activity_name || activity.name}
          </div>
          {activity.name && activity.activity_name && activity.name !== activity.activity_name && (
            <div className="text-[11px] text-gray-500 mt-0.5">{activity.name}</div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onViewFlow}
            className="text-[11px] px-2 py-1 rounded border border-gray-200 hover:border-[#0A3747] hover:bg-gray-50 flex items-center gap-1 text-gray-700"
            data-testid={`viewflow-${activity.code}`}
            title="Ver diagrama de flujo"
          >
            <GitBranch size={11} /> Flujo
          </button>
          {/* v3.9.4 bis · Botón Formulario de fiscalización */}
          <button
            onClick={() => setShowFormLink(true)}
            className="text-[11px] px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1"
            data-testid={`fisc-form-btn-${activity.code}`}
            title="Vincular formulario de fiscalización"
          >
            📋 Formulario
          </button>
          {canEdit && (
            <button
              onClick={(e) => onEdit(e.currentTarget)}
              className="text-[11px] px-2 py-1 rounded bg-[#0A3747] text-white hover:bg-[#135166] flex items-center gap-1"
              data-testid={`edit-${activity.code}`}
            >
              Editar
            </button>
          )}
        </div>
      </div>

      {showFormLink && (
        <FiscFormLinkModal
          st={{ name: activity.activity_name || activity.name, code: activity.code }}
          onClose={() => setShowFormLink(false)}
        />
      )}

      {/* Chips de reglas */}
      <div className="mt-2 flex items-center flex-wrap gap-1.5">
        <span
          className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
            activity.scheduling_mode === "automatic" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"
          }`}
        >
          {SCHED_LABEL[activity.scheduling_mode]}
        </span>
        <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${MATERIAL_COLOR[activity.material_control]}`}>
          {MATERIAL_LABEL[activity.material_control]}
        </span>
        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
          SLA {activity.sla_p1_hours}h / {activity.sla_p2_hours}h / {activity.sla_p3_hours}h
        </span>
        {activity.photo_report_required && <MiniFlag icon={Camera} label="Foto" />}
        {activity.ai_gps_validation && <MiniFlag icon={Cpu} label="IA+GPS" tone="purple" />}
        {activity.requires_equipment_return && <MiniFlag icon={RotateCcw} label="Devuelve" tone="red" />}
        {activity.whatsapp_confirmation && <MiniFlag icon={MessageCircle} label="WA" tone="green" />}
        {activity.pvp_configurable && <MiniFlag icon={DollarSign} label="PVP" tone="yellow" />}
        {(activity.applies_to_client_types || []).slice(0, 3).map((ct) => (
          <span key={ct} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
            {clientMap[ct] || ct}
          </span>
        ))}
        {(activity.applies_to_client_types || []).length > 3 && (
          <span className="text-[9px] text-gray-400">+{activity.applies_to_client_types.length - 3}</span>
        )}
      </div>
    </div>
  );
}

function MiniFlag({ icon: Icon, label, tone = "navy" }) {
  const tones = {
    navy: "bg-[#0A3747] text-[#E0E621]",
    purple: "bg-purple-100 text-purple-800",
    red: "bg-red-100 text-red-800",
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
  };
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] font-mono px-1.5 py-0.5 rounded ${tones[tone] || tones.navy}`}>
      <Icon size={9} /> {label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VISTA TABLA (mantenida pero con nueva nomenclatura)
// ═══════════════════════════════════════════════════════════════════
function TableView({ items, loading, canEdit, onEdit, onViewFlow, onOpenRecipe, catalog }) {
  const [formLinkTarget, setFormLinkTarget] = useState(null);   // v3.9.4 bis
  const clientLabel = useMemo(() => {
    const m = {};
    // v101.133.0 · Etiquetas de tipos de cliente de la suscripción + segmentos anteriores
    [...(catalog?.client_types || []), ...(catalog?.legacy_client_types || [])].forEach((c) => { m[c.code] = c.label; });
    return m;
  }, [catalog]);
  const lobLabel = useMemo(() => {
    const m = {};
    (catalog?.lines_of_business || []).forEach((l) => { m[l.code] = l.label; });
    return m;
  }, [catalog]);

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>;
  }
  if (!items.length) {
    return <div className="py-20 text-center text-sm text-gray-500" data-testid="empty-soltypes">Sin actividades encontradas.</div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#0A3747] text-white text-left">
            <tr>
              <th className="px-3 py-3 font-medium">Código / {TAX.actividad}</th>
              <th className="px-3 py-3 font-medium">{TAX.linea}</th>
              <th className="px-3 py-3 font-medium">{TAX.servicio}</th>
              <th className="px-3 py-3 font-medium">Tecnología</th>
              <th className="px-3 py-3 font-medium">Agend.</th>
              <th className="px-3 py-3 font-medium">Materiales</th>
              <th className="px-3 py-3 font-medium text-center">Reglas</th>
              <th className="px-3 py-3 font-medium">SLA (h)</th>
              <th className="px-3 py-3 font-medium">Aplica a</th>
              <th className="px-3 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`soltype-row-${it.code}`}>
                <td className="px-3 py-2.5 min-w-[220px]">
                  <div className="font-mono text-[10px] text-gray-500">{it.code}</div>
                  <div className="font-medium text-sm">{it.activity_name || it.name}</div>
                </td>
                <td className="px-3 py-2.5 text-xs">
                  {lobLabel[it.line_of_business] || "—"}
                </td>
                <td className="px-3 py-2.5 text-xs">{it.service_name || "—"}</td>
                <td className="px-3 py-2.5">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">{it.technology?.slice(0, 12)}</span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${it.scheduling_mode === "automatic" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>
                    {SCHED_LABEL[it.scheduling_mode]}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${MATERIAL_COLOR[it.material_control]}`}>
                    {MATERIAL_LABEL[it.material_control]}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1 justify-center">
                    <FlagIcon active={it.photo_report_required} icon={Camera} label="Foto" />
                    <FlagIcon active={it.ai_gps_validation} icon={Cpu} label="IA+GPS" />
                    <FlagIcon active={it.requires_equipment_return} icon={RotateCcw} label="Devuelve equipo" />
                    <FlagIcon active={it.whatsapp_confirmation} icon={MessageCircle} label="WhatsApp" />
                    <FlagIcon active={it.pvp_configurable} icon={DollarSign} label="PVP configurable" />
                  </div>
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px]">
                  <div><span className="text-red-600">P1:</span> {it.sla_p1_hours}h</div>
                  <div><span className="text-amber-600">P2:</span> {it.sla_p2_hours}h</div>
                  <div><span className="text-blue-600">P3:</span> {it.sla_p3_hours}h</div>
                </td>
                <td className="px-3 py-2.5 max-w-[180px]">
                  <div className="flex flex-wrap gap-0.5">
                    {(it.applies_to_client_types || []).map((c) => (
                      <span key={c} className="text-[9px] px-1 py-0.5 rounded bg-gray-100 text-gray-700">
                        {clientLabel[c] || c}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => onViewFlow(it)}
                      className="text-[11px] text-[#0A3747] underline hover:no-underline flex items-center gap-1"
                      data-testid={`viewflow-${it.code}`}
                    >
                      <GitBranch size={10} /> Flujo
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => onOpenRecipe(it)}
                        className="text-[11px] text-emerald-700 underline hover:no-underline flex items-center gap-1"
                        data-testid={`recipe-${it.code}`}
                      >
                        <Package size={10} /> Receta
                      </button>
                    )}
                    {/* v3.9.4 bis · Botón Formulario */}
                    <button
                      onClick={() => setFormLinkTarget(it)}
                      className="text-[11px] text-blue-700 underline hover:no-underline flex items-center gap-1"
                      data-testid={`fisc-form-btn-${it.code}`}
                    >
                      📋 Formulario
                    </button>
                    {canEdit && (
                      <button
                        onClick={(e) => onEdit(it, e.currentTarget)}
                        className="text-[11px] text-[#0A3747] underline hover:no-underline"
                        data-testid={`edit-${it.code}`}
                      >
                        Editar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {formLinkTarget && (
        <FiscFormLinkModal
          st={{ name: formLinkTarget.name || formLinkTarget.activity_name, code: formLinkTarget.code }}
          onClose={() => setFormLinkTarget(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// RULES WIZARD POPOVER · v2.8.3 — 4 pasos: Cliente & Notif, Operación, Material, SLA
// Emerge del botón "Editar" clickeado; fallback a EditModal (form completo).
// ═══════════════════════════════════════════════════════════════════
function RulesWizard({ item, catalog, anchorRef, onClose, onSaved, onOpenFull, saveEndpoint }) {
  const isGrouper = (item.children || []).filter(c => c.active !== false).length > 0;
  const [form, setForm] = useState({
    scheduling_mode: item.scheduling_mode,
    // v3.9.14 · Se mantienen los campos legacy en el payload para compat backend
    // pero ya no son editables. La UI de "Control de material" fue eliminada.
    material_control: item.material_control,
    material_max_required: !!item.material_max_required,
    material_min_required: !!item.material_min_required,
    material_gratuito: !!item.material_gratuito,
    material_max_mode: item.material_max_mode || (item.material_max_required ? "obligatorio" : "no_aplica"),
    material_min_mode: item.material_min_mode || (item.material_min_required ? "obligatorio" : "no_aplica"),
    material_gratuito_mode: item.material_gratuito_mode || (item.material_gratuito ? "obligatorio" : "no_aplica"),
    tecnologia: item.tecnologia || "",
    material_recipe_categories: item.material_recipe_categories || [],
    photo_report_required: item.photo_report_required,
    client_notification: item.client_notification,
    ai_gps_validation: item.ai_gps_validation,
    requires_equipment_return: item.requires_equipment_return,
    whatsapp_confirmation: item.whatsapp_confirmation,
    pvp_configurable: item.pvp_configurable,
    requires_atc_validation: item.requires_atc_validation,
    sla_p1_hours: item.sla_p1_hours,
    sla_p2_hours: item.sla_p2_hours,
    sla_p3_hours: item.sla_p3_hours,
    applies_to_client_types: item.applies_to_client_types || [],
    // v3.9.14 · Mapeo Odoo activity_type
    odoo_activity_type: item.odoo_activity_type || null,
    // v57.1.0 · Tipo de flujo de aprovisionamiento
    provisioning_flow_type_id: item.provisioning_flow_type_id || null,
  });
  const [saving, setSaving] = useState(false);
  // v3.9.4 · Modal de clonación (Copy-Paste independiente)
  const [showClone, setShowClone] = useState(false);
  // v4.1.0 · Batch aplicar a descendientes
  const [showBatch, setShowBatch] = useState(false);
  // v3.9.14 · Cargar activity_types de Odoo dinámicamente
  const [odooActivities, setOdooActivities] = useState([]);
  const [provisioningFlowTypes, setProvisioningFlowTypes] = useState([]);
  useEffect(() => {
    api.get("/v1/mesa/solution-types/odoo-activities")
      .then(({ data }) => setOdooActivities(data.items || []))
      .catch(() => {});
    api.get("/v1/fisc-matrix/provisioning-flow-types")
      .then(({ data }) => setProvisioningFlowTypes(data.items || []))
      .catch(() => {});
  }, []);

  const toggleClientType = (code) => {
    const set = new Set(form.applies_to_client_types);
    if (set.has(code)) set.delete(code); else set.add(code);
    setForm({ ...form, applies_to_client_types: [...set] });
  };
  const toggleRecipeCat = (code) => {
    const set = new Set(form.material_recipe_categories || []);
    if (set.has(code)) set.delete(code); else set.add(code);
    setForm({ ...form, material_recipe_categories: [...set] });
  };

  const doSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (saveEndpoint) {
        // v99.2.0 · Enviar null explícito para borrar (patrón SENTINEL)
        const payload = { ...form };
        if (payload.odoo_activity_type === "" || payload.odoo_activity_type === undefined) {
          payload.odoo_activity_type = null;
        }
        if (payload.provisioning_flow_type_id === "" || payload.provisioning_flow_type_id === undefined) {
          payload.provisioning_flow_type_id = null;
        }
        await api.put(saveEndpoint, payload);
        // v99.2.0 · Verify: releer el nodo y confirmar que lo leído coincide
        const { data: verify } = await api.get(`${saveEndpoint}`);
        const savedRules = verify?.rules || {};
        const saved_odoo = savedRules.odoo_activity_type || verify?.odoo_activity_type || null;
        const sent_odoo = payload.odoo_activity_type || null;
        if (sent_odoo !== saved_odoo) {
          toast.error(`Error de verificación: odoo_activity_type enviado='${sent_odoo}' pero guardado='${saved_odoo}'`);
          setSaving(false);
          return;
        }
      } else {
        // SolutionType → PATCH principal + PATCH dedicado para odoo_activity_type
        const { odoo_activity_type: newAct, ...restForm } = form;
        await api.patch(`/v1/solution-types/${item.id}`, restForm);
        // v3.9.14 · Detección de cambio robusta: se dispara siempre que el valor
        // del form difiera del valor actual del item (incluyendo limpieza a null)
        const prevAct = item.odoo_activity_type ?? null;
        const nextAct = newAct ?? null;
        if (prevAct !== nextAct) {
          await api.patch(`/v1/mesa/solution-types/${item.id}/odoo-activity`, {
            odoo_activity_type: nextAct,
          });
        }
      }
      toast.success(`${TAX.actividad} actualizada`);
      onSaved({ ...item, ...form });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const clientTypes = catalog?.client_types || [];
  const materialCategories = catalog?.material_categories || [];

  return (
    <>
    <WizardPopover
      open={true}
      onOpenChange={(o) => !o && onClose()}
      anchorRef={anchorRef}
      title={<span className="flex items-center gap-2">Editar reglas · {item.activity_name || item.name}<a href="/centro-documentacion#tipos-solucion#5" title="Manual: Panel de reglas" className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#0A3747]/10 hover:bg-[#0A3747]/20 transition" data-testid="help-rules-panel"><HelpCircle size={12} className="text-[#0A3747]" /></a></span>}
      subtitle={item.code}
      icon={Workflow}
      saving={saving}
      saveLabel="Guardar"
      onSave={doSave}
      onOpenFull={onOpenFull}
      testid="rules-wizard"
      width={560}
      extraFooterActions={(
        <>
          <button
            type="button"
            onClick={() => setShowClone(true)}
            className="px-2.5 py-1.5 border border-yellow-500 text-yellow-800 bg-yellow-50 text-[11px] rounded hover:bg-yellow-100 flex items-center gap-1"
            data-testid="wizard-btn-clone"
            title="Copiar Receta / SLA / Formulario desde otro nodo"
          >
            <Copy size={11} /> Duplicar
          </button>
          <button
            type="button"
            onClick={() => setShowBatch(true)}
            className="px-2.5 py-1.5 border border-cyan-500 text-cyan-800 bg-cyan-50 text-[11px] rounded hover:bg-cyan-100 flex items-center gap-1"
            data-testid="wizard-btn-batch"
            title="Aplicar esta configuración a TODOS los descendientes"
          >
            <GitBranch size={11} /> A hijos
          </button>
        </>
      )}
      steps={[
        // v101.132.0 · Niveles independientes: un nodo con hijos se configura igual que un tipo final
        //              (activity type, flujo, tecnología, SLA). No se hereda a los hijos ni depende del padre.
        ...(isGrouper ? [{
          key: "info",
          title: "Nivel",
          icon: Info,
          content: (
            <div className="p-4 border border-blue-200 bg-blue-50 rounded-md text-sm text-blue-800" data-testid="rw-grouper-info">
              <div className="font-semibold mb-2">Nivel con subtipos</div>
              <p>Este nodo tiene {(item.children || []).filter(c => c.active !== false).length} hijos activos.</p>
              <p className="mt-1">Puede tener su <b>propio activity type</b> y configuración: cada nivel es independiente
                (no hereda del nivel anterior ni se copia a los hijos).</p>
              <p className="mt-1 text-[11px] text-blue-600">Si tiene activity type, se ofrece en «Reasignar tipo de solución» de las OTs con ese
                activity type. La asignación automática desde Odoo sigue usando los tipos sin hijos.</p>
            </div>
          ),
        }] : []),
        // 1 · CLIENTE & NOTIFICACIÓN
        {
          key: "cliente",
          title: "Cliente",
          icon: Users,
          content: (
            <div className="space-y-3">
              <AppliesToClientTypes form={form} setForm={setForm} catalog={catalog} testidPrefix="rw-" />
              <ClientNotificationField form={form} setForm={setForm} testidPrefix="rw-" />
            </div>
          ),
        },
        // 2 · OPERACIÓN
        {
          key: "operacion",
          title: "Operación",
          icon: Cpu,
          content: (
            <div className="space-y-3">
              <OperationModeFields form={form} setForm={setForm} testidPrefix="rw-" />
              <OperationalTogglesGrid form={form} setForm={setForm} testidPrefix="rw-" />
              <ProvisioningFlowField form={form} setForm={setForm} provisioningFlowTypes={provisioningFlowTypes} testidPrefix="rw-" />
            </div>
          ),
        },
        // 3 · MATERIAL / ACTIVIDAD ODOO
        {
          key: "material",
          title: "Actividad + Materiales",
          icon: Package,
          content: (
            <div className="space-y-3">
              <OdooActivityMapField form={form} setForm={setForm} odooActivities={odooActivities} testidPrefix="rw-" />
              <div className="border rounded-md p-2.5 border-amber-300 bg-amber-50">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">Receta de Materiales</div>
                <p className="text-[11px] mt-1 text-amber-900">
                  Los materiales (Categoría → Producto) se administran desde el botón <b>&quot;Receta&quot;</b> en la tabla o
                  desde <b>&quot;Editar Receta&quot;</b> en el formulario completo.
                </p>
              </div>
            </div>
          ),
        },
        // 4 · SLA + RESUMEN
        {
          key: "sla",
          title: "SLA",
          icon: Clock,
          content: (
            <div className="space-y-3">
              <SLAFields form={form} setForm={setForm} testidPrefix="rw-" />
              <RulesSummary form={form} />
            </div>
          ),
        },
      ]}
    />
    {showClone && (
      <CloneConfigModal
        targetNode={item}
        onClose={() => setShowClone(false)}
        onCloned={(res) => {
          setShowClone(false);
          toast.success(`Configuración duplicada desde "${res.cloned_from.name}"`);
          onSaved({ ...item, __refresh: true });
        }}
      />
    )}
    {showBatch && (
      <ApplyToDescendantsModal
        sourceNode={item}
        onClose={() => setShowBatch(false)}
        onApplied={(res) => {
          setShowBatch(false);
          toast.success(`Config aplicada a ${res.count_affected} descendientes`);
          onSaved({ ...item, __refresh: true });
        }}
      />
    )}
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════
// MODAL EDICIÓN (mantiene lógica original con etiquetas actualizadas)
// ═══════════════════════════════════════════════════════════════════
function EditModal({ item, catalog, onClose, onSaved, onOpenRecipe }) {
  const [odooActivities, setOdooActivities] = useState([]);
  const [provisioningFlowTypes, setProvisioningFlowTypes] = useState([]);
  const [showClone, setShowClone] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  useEffect(() => {
    api.get("/v1/mesa/solution-types/odoo-activities")
      .then(({ data }) => setOdooActivities(data.items || []))
      .catch(() => {});
    api.get("/v1/fisc-matrix/provisioning-flow-types")
      .then(({ data }) => setProvisioningFlowTypes(data.items || []))
      .catch(() => {});
  }, []);
  const [form, setForm] = useState({
    scheduling_mode: item.scheduling_mode,
    // v3.9.14 · Sección legacy "Control de material · comportamiento" eliminada.
    // Los flags se mantienen en el payload por compat backend, pero NO se editan
    // desde este formulario. La única gestión de materiales es vía RecipeMaterialsModal.
    material_control: item.material_control,
    material_max_required: !!item.material_max_required,
    material_min_required: !!item.material_min_required,
    material_gratuito: !!item.material_gratuito,
    tecnologia: item.tecnologia || "",
    material_recipe_categories: item.material_recipe_categories || [],
    photo_report_required: item.photo_report_required,
    client_notification: item.client_notification,
    ai_gps_validation: item.ai_gps_validation,
    requires_equipment_return: item.requires_equipment_return,
    whatsapp_confirmation: item.whatsapp_confirmation,
    pvp_configurable: item.pvp_configurable,
    requires_atc_validation: item.requires_atc_validation,
    sla_p1_hours: item.sla_p1_hours,
    sla_p2_hours: item.sla_p2_hours,
    sla_p3_hours: item.sla_p3_hours,
    applies_to_client_types: item.applies_to_client_types || [],
    // v3.9.0 · Mapeo directo con Odoo project.task.activity_type
    odoo_activity_type: item.odoo_activity_type || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      // Separar el mapeo Odoo (endpoint dedicado en mesa técnica)
      const { odoo_activity_type, ...restForm } = form;
      await api.patch(`/v1/solution-types/${item.id}`, restForm);
      // Actualizar mapeo activity_type si cambió
      if ((odoo_activity_type || "") !== (item.odoo_activity_type || "")) {
        try {
          await api.patch(`/v1/mesa/solution-types/${item.id}/odoo-activity`, {
            odoo_activity_type: odoo_activity_type || null,
          });
        } catch (e) {
          console.warn("odoo_activity_type patch failed:", e);
        }
      }
      toast.success(`${TAX.actividad} actualizada`);
      const updatedItem = { ...item, ...form };
      onSaved(updatedItem);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const toggleClientType = (code) => {
    const set = new Set(form.applies_to_client_types);
    if (set.has(code)) set.delete(code); else set.add(code);
    setForm({ ...form, applies_to_client_types: [...set] });
  };

  return (
    <PageForm
      title={item.activity_name || item.name}
      breadcrumb={<span className="flex items-center gap-2">Tipos de Solución › {item.service_name || item.code} › Editar reglas<a href="/centro-documentacion#tipos-solucion#5" title="Manual: Panel de reglas" className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#0A3747]/10 hover:bg-[#0A3747]/20 transition" data-testid="help-rules-page"><HelpCircle size={12} className="text-[#0A3747]" /></a></span>}
      subtitle={item.code}
      onBack={onClose}
      maxWidth="max-w-3xl"
      testId="edit-soltype-page"
    >
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm" data-testid="edit-soltype-modal">
        <form onSubmit={submit} className="p-6 space-y-4 text-sm">
          <SolutionRulesFieldsBody
            form={form}
            setForm={setForm}
            catalog={catalog}
            odooActivities={odooActivities}
            provisioningFlowTypes={provisioningFlowTypes}
            onOpenRecipe={onOpenRecipe}
            onCopyRecipe={() => setShowClone(true)}
            inheritedFromName={item.inherits_recipe ? getParentName(item) : null}
            hasOwnRecipe={!!item.solution_type_id}
            testidPrefix="edit-"
          />

          <div className="flex gap-2 pt-2 border-t sticky bottom-0 bg-white pt-3">
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-40 flex items-center justify-center gap-2" data-testid="save-soltype">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              Guardar cambios
            </button>
            <button
              type="button"
              onClick={() => setShowClone(true)}
              className="px-3 py-2 border border-yellow-500 text-yellow-800 bg-yellow-50 text-xs rounded hover:bg-yellow-100 flex items-center gap-1.5"
              data-testid="btn-clone-config"
              title="Copiar Receta / SLA / Formulario desde otra solución"
            >
              <Copy size={13} /> Duplicar configuración
            </button>
            <button
              type="button"
              onClick={() => setShowBatch(true)}
              className="px-3 py-2 border border-cyan-500 text-cyan-800 bg-cyan-50 text-xs rounded hover:bg-cyan-100 flex items-center gap-1.5"
              data-testid="btn-apply-to-descendants"
              title="Aplicar esta configuración a TODOS los descendientes"
            >
              <GitBranch size={13} /> Aplicar a hijos
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50">Cancelar</button>
          </div>
        </form>
      </div>

      {showClone && (
        <CloneConfigModal
          targetNode={item}
          onClose={() => setShowClone(false)}
          onCloned={(res) => {
            setShowClone(false);
            toast.success(`Configuración duplicada desde "${res.cloned_from.name}"`);
            // Refresca el item con los nuevos valores del backend (mejor recargar padre)
            onSaved({ ...item, __refresh: true });
          }}
        />
      )}
      {showBatch && (
        <ApplyToDescendantsModal
          sourceNode={item}
          onClose={() => setShowBatch(false)}
          onApplied={(res) => {
            setShowBatch(false);
            toast.success(`Config aplicada a ${res.count_affected} descendientes`);
            onSaved({ ...item, __refresh: true });
          }}
        />
      )}
    </PageForm>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1.5 uppercase tracking-wider">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" data-testid={testid}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <label className={`flex items-center justify-between border rounded px-2 py-1.5 cursor-pointer text-xs ${value ? "border-[#0A3747] bg-[#0A3747]/5" : "border-gray-200"}`}>
      <span>{label}</span>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" data-testid={testid} />
    </label>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VISTA FLUJOS (mantenida sin cambios funcionales)
// ═══════════════════════════════════════════════════════════════════
function FlowGalleryView({ items, loading, onViewFlow, catalog }) {
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const hiddenRef = useRef(null);

  // Mapa de label + estilo por línea de negocio
  const lobIndex = useMemo(() => {
    const m = {};
    (catalog?.lines_of_business || []).forEach((l) => { m[l.code] = l; });
    return m;
  }, [catalog]);

  // Agrupar items por Línea de Negocio > Servicio (nueva taxonomía Feb-2026)
  const grouped = useMemo(() => {
    const g = {};
    items.forEach((it) => {
      const lob = it.line_of_business || "operaciones_clientes";
      const svc = it.service_name || "Sin servicio";
      g[lob] = g[lob] || {};
      g[lob][svc] = g[lob][svc] || [];
      g[lob][svc].push(it);
    });
    return g;
  }, [items]);

  const downloadAllPDF = async () => {
    if (downloading || !items.length) return;
    setDownloading(true);
    setProgress({ done: 0, total: items.length });
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 30;

      // Portada
      pdf.setFillColor(10, 55, 71);
      pdf.rect(0, 0, pageW, pageH, "F");
      pdf.setTextColor(224, 230, 33);
      pdf.setFontSize(28);
      pdf.text("Flujos de Decisión", pageW / 2, pageH / 2 - 60, { align: "center" });
      pdf.setFontSize(20);
      pdf.text("Alfa-Field Connect · Tipos de Solución", pageW / 2, pageH / 2 - 20, { align: "center" });
      pdf.setFontSize(11);
      pdf.setTextColor(255, 255, 255);
      pdf.text(`${items.length} actividades · ${new Date().toLocaleDateString("es-EC")}`, pageW / 2, pageH / 2 + 20, { align: "center" });
      pdf.setFontSize(9);
      pdf.setTextColor(200, 200, 200);
      pdf.text("Jerarquía: Línea de Negocio › Servicio › Actividad Específica", pageW / 2, pageH / 2 + 45, { align: "center" });

      let counter = 0;
      for (const [lobCode, services] of Object.entries(grouped)) {
        const lobInfo = lobIndex[lobCode] || { label: lobCode };
        for (const [svcName, activities] of Object.entries(services)) {
          for (const item of activities) {
            counter++;
            const svgString = buildSvgString(item);
            const dataUrl = await svgToPngDataUrl(svgString, 800);
            pdf.addPage();
            pdf.setFillColor(10, 55, 71);
            pdf.rect(0, 0, pageW, 62, "F");
            // Línea de Negocio
            pdf.setTextColor(224, 230, 33);
            pdf.setFontSize(8);
            pdf.text(lobInfo.label.toUpperCase() + "  ›  " + svcName.toUpperCase(), margin, 18);
            // Código
            pdf.setTextColor(180, 180, 180);
            pdf.setFontSize(8);
            pdf.text(item.code, margin, 34);
            // Nombre de actividad
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(14);
            pdf.text(truncate(item.activity_name || item.name, 65), margin, 52);
            // Página
            pdf.setFontSize(8);
            pdf.setTextColor(180, 180, 180);
            pdf.text(`${counter} / ${items.length}`, pageW - margin, 18, { align: "right" });

            const imgW = pageW - margin * 2;
            const dims = await getImgDims(dataUrl);
            let imgH = (dims.h / dims.w) * imgW;
            const availableH = pageH - 75 - margin - 40;
            let finalW = imgW;
            if (imgH > availableH) {
              imgH = availableH;
              finalW = (dims.w / dims.h) * imgH;
            }
            const xPos = (pageW - finalW) / 2;
            pdf.addImage(dataUrl, "PNG", xPos, 75, finalW, imgH);

            setProgress({ done: counter, total: items.length });
            await new Promise((r) => setTimeout(r, 0));
          }
        }
      }

      pdf.save(`Flujos_Decision_Alfa_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`PDF generado con ${items.length} flujos`);
    } catch (err) {
      console.error(err);
      toast.error("Error al generar el PDF");
    } finally {
      setDownloading(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>;
  }
  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-start justify-between gap-3">
        <div className="text-sm text-gray-700 max-w-3xl">
          <strong className="text-[#0A3747]">Diagramas de flujo de decisión</strong> — Cada {TAX.actividad.toLowerCase()} tiene su
          propio flujo, agrupado por {TAX.linea.toLowerCase()} y {TAX.servicio.toLowerCase()}.
        </div>
        <button
          onClick={downloadAllPDF}
          disabled={downloading || !items.length}
          className="flex items-center gap-2 px-4 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="download-all-pdf"
        >
          {downloading ? (
            <><Loader2 className="animate-spin" size={14} /> Generando... {progress.done}/{progress.total}</>
          ) : (
            <><FileText size={14} /> Descargar TODOS en PDF</>
          )}
        </button>
      </div>
      <div ref={hiddenRef} style={{ position: "absolute", left: "-99999px", top: "-99999px", width: 800 }} aria-hidden />

      {/* Galería agrupada por Línea de Negocio > Servicio */}
      {Object.entries(grouped).map(([lobCode, services]) => {
        const lobInfo = lobIndex[lobCode] || { label: lobCode, style: {} };
        const IconComp = LOB_ICONS[lobInfo.style?.icon] || Briefcase;
        const bg = lobInfo.style?.color || "#0A3747";
        const accent = lobInfo.style?.accent || "#E0E621";
        const totalActs = Object.values(services).reduce((a, b) => a + b.length, 0);
        return (
          <div key={lobCode} data-testid={`flow-lob-${lobCode}`} className="space-y-3">
            {/* Header de Línea de Negocio */}
            <div
              className="rounded-lg px-5 py-3 flex items-center gap-3"
              style={{ background: bg, color: "#fff" }}
            >
              <div
                className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ background: accent, color: bg }}
              >
                <IconComp size={18} strokeWidth={2.25} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] opacity-70">{TAX.linea}</div>
                <div className="text-base font-bold" style={{ fontFamily: "Chivo" }}>{lobInfo.label}</div>
              </div>
              <span className="text-[10px] font-mono px-2.5 py-1 rounded-full font-bold" style={{ background: accent, color: bg }}>
                {totalActs} flujos
              </span>
            </div>

            {/* Servicios dentro de la línea */}
            {Object.entries(services).map(([svcName, acts]) => (
              <div key={`${lobCode}::${svcName}`} className="pl-4 border-l-4" style={{ borderColor: `${accent}80` }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[9px] font-mono uppercase tracking-widest text-gray-400">{TAX.servicio}</span>
                  <span className="font-semibold text-[#0A3747] text-sm" style={{ fontFamily: "Chivo" }}>{svcName}</span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{acts.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {acts.map((it) => (
                    <button
                      key={it.id}
                      onClick={() => onViewFlow(it)}
                      className="text-left border border-gray-200 rounded-lg p-4 bg-white hover:border-[#0A3747] hover:shadow-md transition group"
                      data-testid={`flow-card-${it.code}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-[10px] text-gray-500">{it.code}</div>
                          <div className="text-[9px] font-mono uppercase tracking-widest text-gray-400 mt-0.5">{TAX.actividad}</div>
                          <div className="text-sm font-semibold mt-0.5 leading-tight">{it.activity_name || it.name}</div>
                        </div>
                        <GitBranch className="text-gray-400 group-hover:text-[#0A3747] transition flex-shrink-0" size={18} />
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {it.technology && (
                          <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 rounded font-mono">
                            {it.technology.slice(0, 14)}
                          </span>
                        )}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${it.scheduling_mode === "automatic" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>
                          {it.scheduling_mode === "automatic" ? "Auto" : "Manual"}
                        </span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${MATERIAL_COLOR[it.material_control]}`}>
                          {MATERIAL_LABEL[it.material_control]}
                        </span>
                        {it.ai_gps_validation && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded">IA+GPS</span>}
                        {it.whatsapp_confirmation && <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-800 rounded">WA</span>}
                        {it.requires_equipment_return && <span className="text-[9px] px-1.5 py-0.5 bg-red-100 text-red-800 rounded">Devuelve</span>}
                      </div>
                      <div className="mt-2 text-[10px] text-gray-500 font-mono">
                        SLA: {it.sla_p1_hours}h / {it.sla_p2_hours}h / {it.sla_p3_hours}h
                      </div>
                      <div className="mt-3 text-[11px] text-[#0A3747] font-medium flex items-center gap-1">
                        <GitBranch size={11} /> Ver diagrama de flujo →
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function truncate(s, n) { return s && s.length > n ? s.slice(0, n - 1) + "…" : s || ""; }

function getImgDims(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.width, h: img.height });
    img.src = dataUrl;
  });
}

function FlowModal({ item, onClose, onEdit }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="flow-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b bg-[#0A3747] text-white">
          <div className="flex items-center gap-2">
            <GitBranch size={16} />
            <span className="font-semibold">Flujo de Decisión</span>
          </div>
          <div className="flex items-center gap-3">
            {onEdit && (
              <button onClick={onEdit} className="text-xs bg-[#E0E621] text-[#0A3747] px-3 py-1 rounded font-medium" data-testid="flow-edit-btn">
                Editar reglas
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="close-flow-modal">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto">
          <SolutionTypeFlowDiagram item={item} />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * ExcelTreeView — jerarquía completa importada de jerarquia.xlsx
 * ═══════════════════════════════════════════════════════════════════ */
function ExcelTreeView({ filterText }) {
  const { user, hasPerm } = useAuth();
  const canEdit = hasPerm("M7.solution_types.edit") || hasPerm("M7.solution_types.update");
  const canDelete = hasPerm("M7.solution_types.delete") || hasPerm("M7.solution_types.update");
  const isSuperAdmin = user?.role === "super_admin";
  const [tree, setTree] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [seeding, setSeeding] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [editingNodeFull, setEditingNodeFull] = useState(false);
  const nodeAnchorRef = useRef(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false); // v4.2.1
  // v2.7.7 · Receta directa desde el árbol (abre RecipeMaterialsModal)
  const [recipeItem, setRecipeItem] = useState(null);
  // v4.2.1 · Modales batch invocados desde el menú ⋮ de cualquier fila
  const [cloneTarget, setCloneTarget] = useState(null);
  const [batchTarget, setBatchTarget] = useState(null);
  // v4.3.0 · Enlazar Formulario desde el menú ⋮
  const [formLinkTarget, setFormLinkTarget] = useState(null);
  // v7.5.0 · Auto-Link Masivo
  const [showAutoLink, setShowAutoLink] = useState(false);
  const [healthReport, setHealthReport] = useState(null);
  const [moveNode, setMoveNode] = useState(null);
  const [orphanBanner, setOrphanBanner] = useState(null);

  const openNodeEditor = async (node, anchorEl) => {
    nodeAnchorRef.current = anchorEl || null;
    setEditingNodeFull(false);
    // v2.7.5 · CRITICAL FIX: precargar reglas efectivas del nodo antes de abrir
    // el RulesWizard. Sin esto, item.odoo_activity_type/photo_report_required/…
    // llegarían como undefined y al guardar el PUT reescribiría el JSONB con
    // null (data loss reproducido en iteration_48).
    try {
      const { data } = await api.get(`/v1/solution-tree/${node.id}/rules`);
      const rules = data?.rules || {};
      setEditingNode({ ...node, ...rules });
    } catch (e) {
      // Fallback: abrir con el nodo crudo pero avisar
      toast.error("No se pudieron cargar las reglas del nodo · abriendo sin precarga");
      setEditingNode(node);
    }
  };
  const closeNodeEditor = () => {
    setEditingNode(null);
    setEditingNodeFull(false);
    nodeAnchorRef.current = null;
  };

  const archiveNode = async (node) => {
    if (!window.confirm(`¿Archivar "${node.name}"?\nEl nodo y sus descendientes quedarán inactivos, pero podés recuperarlos activando "Ver archivados".`)) return;
    try {
      await api.delete(`/v1/solution-tree/${node.id}?soft=true`);
      toast.success(`"${node.name}" archivado`);
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 409) {
        // Extraer conteo de OTs del mensaje "N OTs abiertas usan este nodo…"
        const match = detail.match(/^(\d+)\s+OTs?\s+abiertas/i);
        const count = match ? match[1] : "varias";
        const forceConfirm = window.confirm(
          `⚠ ${count} OTs abiertas usan "${node.name}" o sus descendientes.\n\n¿Archivar de todos modos?\nLas OTs NO se modificarán, pero el nodo dejará de aparecer en el catálogo activo.`
        );
        if (!forceConfirm) return;
        try {
          await api.delete(`/v1/solution-tree/${node.id}?soft=true&confirm_archive=true`);
          toast.success(`"${node.name}" archivado (${count} OTs asociadas)`);
          await load();
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Error al archivar con confirmación");
        }
      } else {
        toast.error(detail || "Error al archivar");
      }
    }
  };

  const restoreNode = async (node) => {
    try {
      await api.post(`/v1/solution-tree/${node.id}/restore`);
      toast.success(`"${node.name}" restaurado`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al restaurar");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [treeRes, catRes] = await Promise.all([
        api.get(`/v1/solution-tree${showArchived ? "?include_archived=true" : ""}`),
        api.get("/v1/solution-types/catalog").catch(() => ({ data: null })),
      ]);
      setTree(treeRes.data.tree || []);
      setTotal(treeRes.data.total_nodes || 0);
      setCatalog(catRes.data);
    } catch {
      toast.error("Error cargando árbol Excel");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [showArchived]);

  const reseed = async () => {
    const warningMsg = isSuperAdmin
      ? "⚠ SUPER ADMIN — RE-IMPORTAR EXCEL\n\nEsta acción reconstruye la jerarquía del árbol desde jerarquia.xlsx.\n• Los nodos existentes se actualizarán por nombre+padre.\n• NO se elimina ningún nodo, pero sí se sobrescriben nombres.\n• Las reglas (flujo, receta, materiales) NO se pierden.\n\n¿Confirma la re-importación?"
      : "¿Importar/actualizar la jerarquía desde jerarquia.xlsx?\nLos nodos existentes se actualizarán. No se borrará ningún nodo.";
    if (!window.confirm(warningMsg)) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/v1/solution-tree/seed-from-excel?replace_all=false");
      toast.success(`Import OK · ${data.total_nodes_after} nodos`);
      await load();
    } catch (e) {
      toast.error("Error re-importando: " + (e?.response?.data?.detail || e.message));
    } finally { setSeeding(false); }
  };

  const q = (filterText || "").trim().toLowerCase();
  const filtered = useMemo(() => {
    // v4.2.1 · Filtro por texto + "sólo incompletos"
    if (!q && !showIncompleteOnly) return tree;
    const walk = (n) => {
      const nameMatch = q ? n.name.toLowerCase().includes(q) : true;
      const isIncomplete = !!n.is_incomplete;
      const passes = nameMatch && (!showIncompleteOnly || isIncomplete);
      const kids = (n.children || []).map(walk).filter(Boolean);
      if (passes || kids.length) {
        return { ...n, children: kids, _m: nameMatch && q };
      }
      return null;
    };
    return tree.map(walk).filter(Boolean);
  }, [tree, q, showIncompleteOnly]);

  useEffect(() => {
    if (!q && !showIncompleteOnly) return;
    const all = {};
    const walk = (n) => { all[n.id] = true; (n.children || []).forEach(walk); };
    filtered.forEach(walk);
    setExpanded(all);
  }, [q, showIncompleteOnly, filtered]);

  // v4.2.1 · Contadores globales: reglas configuradas + incompletos
  const rulesStats = useMemo(() => {
    let configured = 0, incomplete = 0, all = 0;
    const walk = (n) => {
      all++;
      if (n.has_rules) configured++;
      if (n.is_incomplete) incomplete++;
      (n.children || []).forEach(walk);
    };
    tree.forEach(walk);
    return { configured, incomplete, total: all };
  }, [tree]);

  // v99.2.0 · Mapa de códigos compartidos: odoo_activity_type → [nombres de nodos]
  const sharedCodeMap = useMemo(() => {
    const map = {};
    const walk = (n) => {
      if (n.odoo_activity_type) {
        if (!map[n.odoo_activity_type]) map[n.odoo_activity_type] = [];
        map[n.odoo_activity_type].push(n.name);
      }
      (n.children || []).forEach(walk);
    };
    tree.forEach(walk);
    return map;
  }, [tree]);

  // v99.1.0 · Mapa fisc_matrix_id → [{id, name}] para detectar recetas compartidas
  const sharedFmstMap = useMemo(() => {
    const map = {};
    const walk = (n) => {
      if (n.fisc_matrix_id) {
        if (!map[n.fisc_matrix_id]) map[n.fisc_matrix_id] = [];
        map[n.fisc_matrix_id].push({ id: n.id, name: n.name });
      }
      (n.children || []).forEach(walk);
    };
    tree.forEach(walk);
    return map;
  }, [tree]);

  const expandAll = () => {
    const all = {};
    const walk = (n) => { all[n.id] = true; (n.children || []).forEach(walk); };
    tree.forEach(walk);
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const countDesc = (n) => {
    let c = 0;
    const walk = (x) => { for (const k of x.children || []) { c++; walk(k); } };
    walk(n);
    return c;
  };

  // v99.3.0 · Handlers de estructura (definidos aquí donde load/setMoveNode están en scope)
  const renameNode = (n) => {
    const newName = prompt("Nuevo nombre:", n.name);
    if (newName && newName.trim() && newName !== n.name) {
      api.patch(`/v1/solution-tree/${n.id}`, { name: newName.trim() })
        .then((r) => {
          toast.success(`Renombrado a "${newName.trim()}"`);
          if (r.data.orphaned_category) {
            toast(`La categoría "${r.data.orphaned_category.name}" quedó sin hijos. Considere archivarla.`, { duration: 8000 });
          }
          load();
        })
        .catch((e) => toast.error(e?.response?.data?.detail || "Error al renombrar"));
    }
  };
  const addChildNode = (n) => {
    const childName = prompt(`Nuevo tipo hijo bajo "${n.name}":`);
    if (childName && childName.trim()) {
      api.post("/v1/solution-tree", { name: childName.trim(), parent_id: n.id })
        .then(() => { toast.success(`"${childName.trim()}" creado bajo "${n.name}"`); load(); })
        .catch((e) => toast.error(e?.response?.data?.detail || "Error al crear hijo"));
    }
  };
  const setDefaultNode = async (n) => {
    try {
      if (n.is_default_for_code) {
        await api.delete(`/v1/solution-tree/${n.id}/set-default`);
        toast.success(`"${n.name}" ya no es predeterminado`);
      } else {
        const resp = await api.put(`/v1/solution-tree/${n.id}/set-default`);
        toast.success(resp.data.message);
        if (resp.data.warnings?.length) {
          resp.data.warnings.forEach(w => toast.warning(w, { duration: 8000 }));
        }
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar predeterminado");
    }
  };
  const independizeNode = async (n) => {
    if (!n.fisc_matrix_id) return;
    const peers = (sharedFmstMap[n.fisc_matrix_id] || []).filter(p => p.id !== n.id);
    if (!peers.length) {
      toast.info("Este nodo ya tiene una receta independiente");
      return;
    }
    const nombres = peers.map(p => p.name).join(", ");
    if (!window.confirm(
      `La receta de "${n.name}" está compartida con: ${nombres}.\n\n` +
      `¿Crear una copia INDEPENDIENTE para "${n.name}"?\n` +
      `(Los otros nodos mantendrán la receta original)`
    )) return;
    try {
      await api.post(`/v1/solution-tree/${n.id}/clone-config`, {
        from_node_id: n.id,
        parts: ["recipe", "form"],
      });
      toast.success(`Receta independizada para "${n.name}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al independizar receta");
    }
  };

  return (
    <div className="space-y-3">
      {/* v4.2.0 · Barra de acciones sobria (reemplaza banner amarillo) */}
      <div className="bg-white rounded-lg border border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <FileText size={14} className="text-slate-400" />
          <span className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
            jerarquia.xlsx
          </span>
          <span className="text-slate-300">·</span>
          <span className="text-xs text-slate-600">
            <strong className="text-slate-900 font-mono tabular-nums">{total}</strong> nodos
            <span className="text-slate-400 mx-1.5">·</span>
            <strong className="text-emerald-700 font-mono tabular-nums">{rulesStats.configured}</strong> con reglas
            <span className="text-slate-400 mx-1.5">·</span>
            <strong className="text-amber-700 font-mono tabular-nums">{rulesStats.incomplete}</strong> incompletos
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowIncompleteOnly(!showIncompleteOnly)}
            className={`px-2.5 py-1 text-[11px] rounded transition flex items-center gap-1.5 ${
              showIncompleteOnly
                ? "bg-amber-100 text-amber-900 font-medium"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="tree-filter-incomplete"
            title="Muestra sólo los nodos donde falta configurar Reglas, Receta o Formulario"
          >
            <CircleDashed size={12} strokeWidth={showIncompleteOnly ? 2.5 : 1.8} />
            {showIncompleteOnly ? "Mostrando incompletos" : "Sólo incompletos"}
          </button>
          <div className="w-px h-4 bg-slate-200 mx-1" />
          <button
            onClick={expandAll}
            className="px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-100 rounded transition"
            data-testid="tree-expand-all"
          >
            Expandir todo
          </button>
          <button
            onClick={collapseAll}
            className="px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-100 rounded transition"
            data-testid="tree-collapse-all"
          >
            Colapsar
          </button>
          <div className="w-px h-4 bg-slate-200 mx-1" />
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`px-2.5 py-1 text-[11px] rounded transition ${
              showArchived
                ? "bg-slate-100 text-slate-800 font-medium"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="tree-show-archived"
          >
            {showArchived ? "Ocultar archivados" : "Ver archivados"}
          </button>
          <button
            onClick={async () => {
              try {
                const { data } = await api.get("/v1/solution-tree/health-report");
                setHealthReport(data);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Error al generar reporte");
              }
            }}
            className="px-3 py-1 text-[11px] bg-rose-600 text-white rounded hover:bg-rose-700 flex items-center gap-1.5 transition"
            data-testid="health-report-btn"
            title="Reporte de salud de configuración"
          >
            <HeartPulse size={11} />
            Salud
          </button>
          {canEdit && (
            <button
              onClick={() => {
                const name = prompt("Nombre de la nueva categoría raíz:");
                if (name && name.trim()) {
                  api.post("/v1/solution-tree", { name: name.trim(), parent_id: null })
                    .then(() => { toast.success(`Categoría "${name.trim()}" creada`); load(); })
                    .catch((e) => toast.error(e?.response?.data?.detail || "Error al crear categoría"));
                }
              }}
              className="px-3 py-1 text-[11px] bg-teal-600 text-white rounded hover:bg-teal-700 flex items-center gap-1.5 transition"
              data-testid="new-category-btn"
            >
              <Plus size={11} />
              Nueva categoría
            </button>
          )}
          <button
            onClick={() => setShowAutoLink(true)}
            className="px-3 py-1 text-[11px] bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1.5 transition"
            data-testid="auto-link-btn"
            title="Vincular automáticamente nodos del árbol con actividades de Odoo por nombre"
          >
            <Sparkles size={11} />
            Auto-Link Odoo
          </button>
          {/* v99.4.0 · Re-importar Excel DESACTIVADO — el árbol se administra desde la pantalla */}
        </div>
      </div>
      {/* Tree */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="excel-tree">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-400">Cargando árbol...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {q ? `Sin resultados para "${filterText}"` : "Sin nodos"}
          </div>
        ) : (
          <div className="py-2">
            {filtered.map((n) => (
              <ExcelTreeNode
                key={n.id}
                node={n}
                expanded={expanded}
                onToggle={toggle}
                countDesc={countDesc}
                q={q}
                depth={0}
                canEdit={canEdit}
                canDelete={canDelete}
                sharedCodeMap={sharedCodeMap}
                sharedFmstMap={sharedFmstMap}
                onEditRules={(node, anchorEl) => openNodeEditor(node, anchorEl)}
                onArchive={archiveNode}
                onRestore={restoreNode}
                onOpenRecipe={async (node) => {
                  // v2.7.7 · Abrir Receta directamente desde el árbol.
                  // Necesita fisc_matrix_id — si no lo tiene, abrir el editor
                  // de reglas donde el admin puede asociarlo.
                  if (!node.fisc_matrix_id) {
                    toast.info("Asocia primero un Tipo de Solución a este nodo desde 'Editar reglas'");
                    await openNodeEditor(node, null);
                    setEditingNodeFull(true);
                    return;
                  }
                  setRecipeItem({
                    id: node.fisc_matrix_id,
                    node_id: node.id,   // v93.0.0 · receta del tipo de solución
                    code: node.solution_type_code,
                    name: node.name || node.solution_type_name,
                  });
                }}
                onCloneConfig={(node) => setCloneTarget(node)}
                onApplyToDescendants={(node) => setBatchTarget(node)}
                onLinkForm={(node) => setFormLinkTarget({ name: node.name, code: node.code || node.solution_type_code })}
                onRename={renameNode}
                onMove={(n) => setMoveNode(n)}
                onAddChild={addChildNode}
                onSetDefault={setDefaultNode}
                onIndependize={independizeNode}
              />
            ))}
          </div>
        )}
      </div>

      {editingNode && editingNodeFull && (
        <TreeNodeRulesModal
          node={editingNode}
          catalog={catalog}
          onClose={closeNodeEditor}
          onSaved={async () => {
            closeNodeEditor();
            await load();
          }}
        />
      )}

      {editingNode && !editingNodeFull && (
        <RulesWizard
          item={{
            ...editingNode,
            id: editingNode.id,
            activity_name: editingNode.name,
            code: editingNode.code,
            scheduling_mode: editingNode.scheduling_mode || "manual",
            client_notification: editingNode.client_notification || "always",
            sla_p1_hours: editingNode.sla_p1_hours ?? 4,
            sla_p2_hours: editingNode.sla_p2_hours ?? 24,
            sla_p3_hours: editingNode.sla_p3_hours ?? 72,
          }}
          catalog={catalog}
          anchorRef={nodeAnchorRef}
          saveEndpoint={`/v1/solution-tree/${editingNode.id}/rules`}
          onClose={closeNodeEditor}
          onOpenFull={() => setEditingNodeFull(true)}
          onSaved={async () => {
            closeNodeEditor();
            await load();
          }}
        />
      )}

      {/* v2.7.7 · Modal Receta directo desde el árbol */}
      {recipeItem && (
        <RecipeMaterialsModal
          solutionType={recipeItem}
          onClose={() => setRecipeItem(null)}
        />
      )}
      {/* v4.2.1 · Modal Duplicar Configuración (invocado desde ⋮) */}
      {cloneTarget && (
        <CloneConfigModal
          targetNode={cloneTarget}
          onClose={() => setCloneTarget(null)}
          onCloned={async (res) => {
            setCloneTarget(null);
            toast.success(`Configuración duplicada desde "${res.cloned_from.name}"`);
            await load();
          }}
        />
      )}
      {/* v4.2.1 · Modal Aplicar a Descendientes (invocado desde ⋮) */}
      {batchTarget && (
        <ApplyToDescendantsModal
          sourceNode={batchTarget}
          onClose={() => setBatchTarget(null)}
          onApplied={async (res) => {
            setBatchTarget(null);
            toast.success(`Config aplicada a ${res.count_affected} descendientes`);
            await load();
          }}
        />
      )}
      {/* v4.3.0 · Modal Enlazar Formulario (invocado desde ⋮) */}
      {formLinkTarget && (
        <FiscFormLinkModal
          st={formLinkTarget}
          onClose={() => setFormLinkTarget(null)}
        />
      )}
      {/* v7.5.0 · Auto-Link Masivo Modal */}
      {showAutoLink && (
        <AutoLinkModal
          onClose={() => setShowAutoLink(false)}
          onApplied={async () => {
            setShowAutoLink(false);
            toast.success("Auto-Link aplicado exitosamente");
            await load();
          }}
        />
      )}
      {/* v99.2.0 · Modal Reporte de Salud */}
      {healthReport && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setHealthReport(null)}>
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-rose-50">
              <div className="flex items-center gap-2">
                <HeartPulse size={16} className="text-rose-600" />
                <span className="font-bold text-sm text-rose-800">Salud de Configuración</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">{healthReport.total_issues} problemas</span>
                <button onClick={() => setHealthReport(null)} className="text-gray-400 hover:text-gray-600" data-testid="health-close">✕</button>
              </div>
            </div>
            <div className="overflow-y-auto p-4 space-y-2 text-[12px]" data-testid="health-report-body">
              {healthReport.total_issues === 0 ? (
                <div className="text-center py-8 text-green-600 font-semibold">Sin problemas detectados</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {Object.entries(healthReport.summary || {}).map(([cat, count]) => (
                      <div key={cat} className="bg-gray-50 rounded px-3 py-1.5 text-[11px]">
                        <span className="font-medium">{cat.replace(/_/g, " ")}</span>: <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                  {healthReport.issues.map((issue, i) => (
                    <div key={i} className={`px-3 py-2 rounded border-l-4 ${
                      issue.severity === "alta" ? "border-red-500 bg-red-50" :
                      issue.severity === "media" ? "border-amber-400 bg-amber-50" :
                      "border-blue-300 bg-blue-50"
                    }`}>
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className={`font-bold uppercase ${
                          issue.severity === "alta" ? "text-red-700" : issue.severity === "media" ? "text-amber-700" : "text-blue-700"
                        }`}>{issue.severity}</span>
                        <span className="text-gray-400">|</span>
                        <span className="font-mono text-gray-500">{issue.category}</span>
                      </div>
                      {issue.node_name && <div className="font-semibold mt-0.5">{issue.node_name}</div>}
                      <div className="text-gray-700 mt-0.5">{issue.detail}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* v99.3.0 · Modal Mover a… */}
      {moveNode && (
        <MoveNodeModal
          node={moveNode}
          tree={tree}
          onClose={() => setMoveNode(null)}
          onMoved={(result) => {
            setMoveNode(null);
            toast.success(`"${moveNode.name}" movido correctamente`);
            if (result.orphaned_category) {
              setOrphanBanner(result.orphaned_category);
              setTimeout(() => setOrphanBanner(null), 12000);
            }
            load();
          }}
        />
      )}
      {/* v99.3.0 · Banner categoría huérfana */}
      {orphanBanner && (
        <div className="fixed bottom-4 right-4 z-40 bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg shadow-lg text-sm max-w-md flex items-center gap-2" data-testid="orphan-banner">
          <span>La categoría <b>"{orphanBanner.name}"</b> quedó sin hijos. Considere archivarla o configurarla.</span>
          <button onClick={() => setOrphanBanner(null)} className="text-amber-500 hover:text-amber-700 font-bold">✕</button>
        </div>
      )}
    </div>
  );
}

function ExcelTreeNode({ node, expanded, onToggle, countDesc, q, depth, canEdit, canDelete, onEditRules, onArchive, onRestore, onOpenRecipe, onCloneConfig, onApplyToDescendants, onLinkForm, sharedCodeMap, sharedFmstMap, onIndependize, onRename, onMove, onAddChild, onSetDefault }) {
  const hasKids = (node.children || []).length > 0;
  const isOpen = !!expanded[node.id];
  const isArchived = node.active === false;
  const Icon = hasKids ? (isOpen ? ChevronDown : ChevronRight) : null;
  const dotColor = depth === 0 ? "#0A3747" : depth === 1 ? "#0D9488" : "#64748B";

  // ── v4.2.1 · Health Check con HERENCIA: verde si el nodo lo tiene O lo hereda
  const rulesEff  = !!node.has_rules_effective  || !!node.has_rules;
  const recipeEff = !!node.has_recipe_effective || !!node.fisc_matrix_id;
  const formEff   = !!node.has_form_effective   || !!node.form_id || !!node.fisc_matrix_id;
  const inhRules  = !!node.inherits_rules;
  const inhRecipe = !!node.inherits_recipe;
  const inhForm   = !!node.inherits_form;

  return (
    <div>
      <div
        className={"group flex items-center gap-2 px-3 py-2 transition " + (node._m ? "bg-slate-50 " : "") + (isArchived ? "opacity-50" : "")}
        style={{
          paddingLeft: 12 + depth * 20,
          borderBottom: "1px solid #EEF2F7",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
        onMouseLeave={(e) => (e.currentTarget.style.background = node._m ? "#F8FAFC" : "transparent")}
      >
        {/* Chevron */}
        <span
          className="w-4 flex-shrink-0 cursor-pointer text-slate-400 hover:text-slate-700"
          onClick={() => hasKids && onToggle(node.id)}
        >
          {Icon ? <Icon size={14} /> : null}
        </span>
        {/* Dot */}
        <span
          style={{ background: dotColor }}
          className="w-2 h-2 rounded-full flex-shrink-0"
        />
        {/* Nombre */}
        <span
          className={"text-sm flex-1 cursor-pointer truncate " + (depth === 0 ? "font-semibold text-slate-900" : depth === 1 ? "font-medium text-slate-800" : "text-slate-700")}
          style={{ letterSpacing: depth === 0 ? "0.02em" : 0 }}
          onClick={() => hasKids && onToggle(node.id)}
        >
          {q && node.name.toLowerCase().includes(q) ? highlight(node.name, q) : node.name}
        </span>
        {/* Contador descendientes */}
        {hasKids && (
          <span className="text-[10px] font-mono text-slate-400 tabular-nums px-1.5">
            {countDesc(node)}
          </span>
        )}
        {/* Health Chips · con distinción propio/heredado */}
        <div className="flex items-center gap-1 mr-1" data-testid={`health-${node.id}`}>
          <HealthChip active={rulesEff}  inherited={inhRules}  label="Reglas"     icon={Settings2}     title={rulesEff ? (inhRules ? "Reglas heredadas del padre" : "Reglas configuradas en este nodo") : "Sin reglas ni herencia"} />
          <HealthChip active={recipeEff} inherited={inhRecipe} label="Receta"     icon={Package}       title={recipeEff ? (inhRecipe ? "Receta heredada del padre" : "Receta vinculada a este nodo") : "Sin receta ni herencia"} />
          <HealthChip active={formEff}   inherited={inhForm}   label="Formulario" icon={ClipboardList} title={formEff ? (inhForm ? "Formulario heredado del padre" : "Formulario propio") : "Sin formulario ni herencia"} />
          {/* v99.1.0 · Badge de flujo propio del nodo */}
          {node.pft_name && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 whitespace-nowrap" title={`Flujo: ${node.pft_name}`}>
              {node.pft_code === "instalacion_fibra_auto" ? "FO" : node.pft_code === "radio_enlace_auto" ? "RE" : node.pft_code === "cambio_equipo_fibra" ? "CE" : node.pft_code === "verificacion_tecnica" ? "VT" : node.pft_code === "traslado_fibra" ? "TR" : node.pft_name?.slice(0, 4)?.toUpperCase()}
            </span>
          )}
          {/* v99.1.0 · Badge Predeterminado */}
          {node.is_default_for_code && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 whitespace-nowrap" title="Nodo predeterminado para este código Odoo">
              ★ DEFAULT
            </span>
          )}
          {/* v99.2.0 · Badge Compartido con */}
          {node.odoo_activity_type && sharedCodeMap?.[node.odoo_activity_type]?.length > 1 && (
            <span
              className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 whitespace-nowrap"
              title={`Código "${node.odoo_activity_type}" compartido con: ${sharedCodeMap[node.odoo_activity_type].filter(n => n !== node.name).join(", ")}`}
            >
              {sharedCodeMap[node.odoo_activity_type].length} nodos
            </span>
          )}
        </div>
        {/* Menú de acciones ⋮ */}
        {(canEdit || canDelete) && (
          <RowActionsMenu
            node={node}
            isArchived={isArchived}
            canEdit={canEdit}
            canDelete={canDelete}
            onEditRules={onEditRules}
            onOpenRecipe={onOpenRecipe}
            onArchive={onArchive}
            onRestore={onRestore}
            onCloneConfig={onCloneConfig}
            onApplyToDescendants={onApplyToDescendants}
            onLinkForm={onLinkForm}
            onSetDefault={onSetDefault}
            sharedFmstMap={sharedFmstMap}
            onIndependize={onIndependize}
            onRename={onRename}
            onMove={onMove}
            onAddChild={onAddChild}
          />
        )}
      </div>
      {hasKids && isOpen ? renderExcelChildren(node.children, expanded, onToggle, countDesc, q, depth, canEdit, canDelete, onEditRules, onArchive, onRestore, onOpenRecipe, onCloneConfig, onApplyToDescendants, onLinkForm, sharedCodeMap, sharedFmstMap, onIndependize, onRename, onMove, onAddChild, onSetDefault) : null}
    </div>
  );
}

/** v4.2.1 · Health chip: verde sólido si es propio, verde tenue con anillo punteado si es heredado. */
function HealthChip({ active, inherited, label, icon: IconCmp, title }) {
  let cls, sw;
  if (!active) {
    cls = "bg-slate-50 text-slate-300";
    sw = 1.8;
  } else if (inherited) {
    // Heredado: verde apagado + borde punteado (indica que viene del padre)
    cls = "bg-emerald-50/60 text-emerald-500 border border-dashed border-emerald-400";
    sw = 2.0;
  } else {
    cls = "bg-emerald-50 text-emerald-600";
    sw = 2.4;
  }
  return (
    <span
      title={title}
      className={`inline-flex items-center justify-center w-6 h-6 rounded-md transition ${cls}`}
      data-testid={`chip-${label.toLowerCase()}${active ? (inherited ? "-inh" : "-on") : "-off"}`}
    >
      <IconCmp size={13} strokeWidth={sw} />
    </span>
  );
}

/** v4.2.1 · Dropdown ⋮ COMPLETO con Editar, Duplicar, Aplicar a hijos, Archivar */
function RowActionsMenu({ node, isArchived, canEdit, canDelete, onEditRules, onOpenRecipe, onArchive, onRestore, onCloneConfig, onApplyToDescendants, onLinkForm, onSetDefault, sharedFmstMap, onIndependize, onRename, onMove, onAddChild }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const items = [];
  // v99.3.0 · Acciones de estructura
  if (canEdit && !isArchived) {
    items.push({
      key: "rename",
      label: "Renombrar",
      icon: Edit,
      onClick: () => onRename && onRename(node),
    });
    items.push({
      key: "move",
      label: "Mover a…",
      icon: Move,
      onClick: () => onMove && onMove(node),
    });
    items.push({
      key: "add-child",
      label: "Agregar tipo hijo",
      icon: Plus,
      onClick: () => onAddChild && onAddChild(node),
    });
    items.push({ separator: true });
  }
  if (canEdit && !isArchived) {
    items.push({
      key: "edit-rules",
      label: "Editar reglas",
      icon: Settings2,
      onClick: (e) => onEditRules && onEditRules(node, btnRef.current || e.currentTarget),
    });
    items.push({
      key: "open-recipe",
      label: node.fisc_matrix_id ? "Editar receta" : "Asociar tipo + receta",
      icon: Package,
      onClick: () => onOpenRecipe && onOpenRecipe(node),
    });
    items.push({
      key: "link-form",
      label: "Enlazar Formulario",
      icon: ClipboardList,
      onClick: () => onLinkForm && onLinkForm(node),
    });
    // v4.2.1 · Acciones batch en el mismo menú
    if (onCloneConfig) {
      items.push({ separator: true });
      items.push({
        key: "clone-config",
        label: "Duplicar configuración",
        icon: Copy,
        onClick: () => onCloneConfig(node),
      });
    }
    if (onApplyToDescendants && (node.children?.length || countDescendantsRaw(node) > 0)) {
      items.push({
        key: "apply-hijos",
        label: "Aplicar a hijos",
        icon: GitBranch,
        onClick: () => onApplyToDescendants(node),
      });
    }
    // v99.1.0 · Marcar como predeterminado
    // v101.132.0 · «Predeterminado» solo en tipos sin hijos (la asignación automática desde Odoo usa tipos finales)
    if (onSetDefault && node.odoo_activity_type && !(node.children || []).some((c) => c.active !== false)) {
      items.push({ separator: true });
      items.push({
        key: "set-default",
        label: node.is_default_for_code ? "Quitar predeterminado" : "★ Marcar predeterminado",
        icon: Star,
        onClick: () => onSetDefault(node),
      });
    }
    // v99.1.0 · Independizar receta (solo si fisc_matrix_id compartido)
    if (onIndependize && node.fisc_matrix_id && sharedFmstMap?.[node.fisc_matrix_id]?.length > 1) {
      items.push({
        key: "independize",
        label: "Independizar receta",
        icon: Unlink,
        onClick: () => onIndependize(node),
      });
    }
  }
  if (canDelete && !isArchived) {
    items.push({ separator: true });
    items.push({
      key: "archive",
      label: "Archivar",
      icon: Archive,
      danger: true,
      onClick: () => onArchive && onArchive(node),
    });
  }
  if (isArchived && canDelete) {
    items.push({
      key: "restore",
      label: "Restaurar",
      icon: RefreshCw,
      onClick: () => onRestore && onRestore(node),
    });
  }
  if (items.length === 0) return null;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`w-7 h-7 rounded-md flex items-center justify-center transition ${
          open ? "bg-slate-200 text-slate-900" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        }`}
        data-testid={`row-menu-${node.id}`}
        title="Acciones"
      >
        <MoreVertical size={15} />
      </button>
      {open && (
        <FloatingPanel
          ref={menuRef}
          anchorRef={btnRef}
          align="end"
          className="bg-white border border-slate-200 rounded-lg shadow-lg min-w-[210px] py-1"
          data-testid={`row-menu-panel-${node.id}`}
        >
          {items.map((it, i) => {
            if (it.separator) {
              return <div key={`sep-${i}`} className="h-px bg-slate-100 my-1" />;
            }
            const Icn = it.icon;
            return (
              <button
                key={it.key}
                onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick && it.onClick(e); }}
                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2.5 ${
                  it.danger
                    ? "text-red-600 hover:bg-red-50"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
                data-testid={`row-menu-item-${it.key}-${node.id}`}
              >
                <Icn size={13} strokeWidth={2} />
                <span>{it.label}</span>
              </button>
            );
          })}
          <div className="h-px bg-slate-100 my-1" />
          <a
            href="/centro-documentacion#tipos-solucion#4"
            className="w-full text-left px-3 py-2 text-xs flex items-center gap-2.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
            data-testid={`row-menu-help-${node.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <HelpCircle size={13} strokeWidth={2} />
            <span>Ayuda: menú de acciones</span>
          </a>
        </FloatingPanel>
      )}
    </div>
  );
}

/** Cuenta descendientes de un nodo (helper para RowActionsMenu). */
function countDescendantsRaw(node) {
  let n = 0;
  const stack = [...(node.children || [])];
  while (stack.length) {
    const x = stack.pop();
    n++;
    if (x.children) stack.push(...x.children);
  }
  return n;
}

function renderExcelChildren(children, expanded, onToggle, countDesc, q, depth, canEdit, canDelete, onEditRules, onArchive, onRestore, onOpenRecipe, onCloneConfig, onApplyToDescendants, onLinkForm, sharedCodeMap, sharedFmstMap, onIndependize, onRename, onMove, onAddChild, onSetDefault) {
  return children.map((c) => (
    <ExcelTreeNode
      key={c.id}
      node={c}
      expanded={expanded}
      onToggle={onToggle}
      countDesc={countDesc}
      q={q}
      depth={depth + 1}
      canEdit={canEdit}
      canDelete={canDelete}
      onEditRules={onEditRules}
      onArchive={onArchive}
      onRestore={onRestore}
      onOpenRecipe={onOpenRecipe}
      onCloneConfig={onCloneConfig}
      onApplyToDescendants={onApplyToDescendants}
      onLinkForm={onLinkForm}
      sharedCodeMap={sharedCodeMap}
      sharedFmstMap={sharedFmstMap}
      onIndependize={onIndependize}
      onRename={onRename}
      onMove={onMove}
      onAddChild={onAddChild}
      onSetDefault={onSetDefault}
    />
  ));
}

function highlight(text, q) {
  const idx = text.toLowerCase().indexOf(q);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[#E0E621] text-[#0A3747] font-bold px-0.5 rounded">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════
// TreeNodeRulesModal — Edición de reglas de negocio por nodo del árbol
// Espejo funcional del EditModal legacy, pero opera contra
// /v1/solution-tree/{id}/rules (cada nodo es independiente, sin herencia).
// ═══════════════════════════════════════════════════════════════════
function TreeNodeRulesModal({ node, catalog, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [hasRules, setHasRules] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [odooActivities, setOdooActivities] = useState([]);
  const [formDefinitions, setFormDefinitions] = useState([]);
  const [provisioningFlowTypes, setProvisioningFlowTypes] = useState([]);
  // v4.2.1 · Modales invocados desde este editor
  const [showCopyRecipe, setShowCopyRecipe] = useState(false);
  const [showRecipe, setShowRecipe] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [rulesRes, actsRes, formsRes, pftRes] = await Promise.all([
          api.get(`/v1/solution-tree/${node.id}/rules`),
          api.get("/v1/mesa/solution-types/odoo-activities").catch(() => ({ data: { items: [] } })),
          api.get("/v1/fiscalizacion/forms").catch(() =>
            api.get("/v1/fiscalizacion/definitions").catch(() => ({ data: { items: [] } }))
          ),
          api.get("/v1/fisc-matrix/provisioning-flow-types").catch(() => ({ data: { items: [] } })),
        ]);
        setForm(rulesRes.data.rules);
        setHasRules(!!rulesRes.data.has_rules);
        setOdooActivities(actsRes.data.items || []);
        setFormDefinitions(formsRes.data.items || formsRes.data || []);
        setProvisioningFlowTypes(pftRes.data.items || []);
      } catch (e) {
        toast.error("Error cargando reglas: " + (e?.response?.data?.detail || e.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [node.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving || !form) return;
    setSaving(true);
    try {
      await api.put(`/v1/solution-tree/${node.id}/rules`, form);
      toast.success("Reglas guardadas para: " + node.name);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const clearRules = async () => {
    if (!window.confirm(`¿Borrar reglas específicas de "${node.name}"?\nEl nodo volverá al comportamiento por defecto.`)) return;
    setSaving(true);
    try {
      await api.delete(`/v1/solution-tree/${node.id}/rules`);
      toast.success("Reglas eliminadas");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    } finally {
      setSaving(false);
    }
  };

  const breadcrumb = (node.path || [node.name]).join(" › ");
  const parentName = getParentName(node);
  const hasOwnRecipe = !!node.fisc_matrix_id;
  const inheritedFromName = (node.inherits_recipe || node.inherits_rules || node.inherits_form) ? parentName : null;

  return (
    <PageForm
      title={node.name}
      breadcrumb={`Tipos de Solución · Árbol · ${breadcrumb}`}
      subtitle={hasRules ? "Reglas específicas configuradas" : "Sin reglas específicas · usando defaults del sistema"}
      onBack={onClose}
      maxWidth="max-w-3xl"
      testId="tree-node-rules-page"
    >
      <div
        className="rounded-3xl p-5"
        style={{
          background: "linear-gradient(145deg, #F1F5F9, #E8ECF1)",
          boxShadow: "8px 8px 20px rgba(163,177,198,0.35), -8px -8px 20px rgba(255,255,255,0.85), inset -1px -1px 3px rgba(0,0,0,0.04), inset 1px 1px 3px rgba(255,255,255,0.6)",
        }}
        data-testid="tree-node-rules-modal"
      >
        {loading || !form ? (
          <div className="p-16 flex justify-center">
            <Loader2 className="animate-spin text-gray-400" />
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 text-sm">
            <SolutionRulesFieldsBody
              form={form}
              setForm={setForm}
              catalog={catalog}
              odooActivities={odooActivities}
              formDefinitions={formDefinitions}
              provisioningFlowTypes={provisioningFlowTypes}
              onOpenRecipe={() => {
                if (!node.fisc_matrix_id) {
                  toast.info("Asocia primero un Tipo de Solución (usa Copiar Receta de…)");
                  return;
                }
                setShowRecipe(true);
              }}
              onCopyRecipe={() => setShowCopyRecipe(true)}
              inheritedFromName={inheritedFromName}
              hasOwnRecipe={hasOwnRecipe}
              testidPrefix="tree-edit-"
            />

            <div
              className="flex gap-2 pt-3 sticky bottom-0 pb-1 rounded-2xl px-3 py-3 mt-2"
              style={{
                background: "linear-gradient(145deg, #F1F5F9, #E8ECF1)",
                boxShadow: "inset 3px 3px 8px rgba(163,177,198,0.25), inset -3px -3px 8px rgba(255,255,255,0.85)",
              }}
            >
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-xl bg-[#0A3747] text-white text-sm font-bold hover:bg-[#135166] disabled:opacity-40 flex items-center justify-center gap-2 shadow-[3px_3px_8px_rgba(10,55,71,0.25),-2px_-2px_5px_rgba(255,255,255,0.9)]"
                data-testid="tree-save-rules"
              >
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                Guardar reglas del nodo
              </button>
              {hasRules && (
                <button
                  type="button"
                  onClick={clearRules}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl border border-red-300 text-red-700 text-sm font-bold hover:bg-red-50 shadow-[2px_2px_5px_rgba(239,68,68,0.15),-2px_-2px_5px_rgba(255,255,255,0.9)]"
                  data-testid="tree-clear-rules"
                >
                  Borrar
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-sm hover:bg-white shadow-[2px_2px_5px_rgba(148,163,184,0.15),-2px_-2px_5px_rgba(255,255,255,0.9)]"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>

      {/* v4.2.1 · Modal Copiar Receta (reusa CloneConfigModal) */}
      {showCopyRecipe && (
        <CloneConfigModal
          targetNode={node}
          onClose={() => setShowCopyRecipe(false)}
          onCloned={(res) => {
            setShowCopyRecipe(false);
            toast.success(`Configuración copiada desde "${res.cloned_from.name}"`);
            onSaved();
          }}
        />
      )}

      {/* v4.2.1 · Modal Materiales directo (si el nodo ya tiene fisc_matrix_id válido) */}
      {showRecipe && node.fisc_matrix_id && node.solution_type_name && (
        <RecipeMaterialsModal
          solutionType={{
            id: node.fisc_matrix_id,
            node_id: node.id,   // v93.0.0 · receta del tipo de solución
            code: node.solution_type_code,
            name: node.name || node.solution_type_name,
          }}
          onClose={() => setShowRecipe(false)}
        />
      )}
      {showRecipe && node.fisc_matrix_id && !node.solution_type_name && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm mx-4 text-center space-y-3">
            <div className="text-amber-500 text-3xl">&#9888;</div>
            <p className="text-sm text-gray-700 font-medium">
              Este nodo tiene una referencia antigua a una receta que ya no existe.
            </p>
            <p className="text-xs text-gray-500">
              Selecciona una receta válida desde <b>Editar Reglas</b> o <b>Copiar Receta de…</b>
            </p>
            <button
              onClick={() => setShowRecipe(false)}
              className="px-4 py-2 rounded-lg bg-[#0A3747] text-white text-xs font-semibold hover:bg-[#0A3747]/90 transition"
              data-testid="orphan-recipe-close-btn"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </PageForm>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * CloneConfigModal · v3.9.4
 * Copia Receta / SLA / Formulario desde otro nodo del árbol como copia
 * INDEPENDIENTE. Llama a POST /v1/solution-tree/{node_id}/clone-config
 * ═════════════════════════════════════════════════════════════════════ */
function CloneConfigModal({ targetNode, onClose, onCloned }) {
  const [q, setQ] = useState("");
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceId, setSourceId] = useState(null);
  const [parts, setParts] = useState({ rules: true, form: true, recipe: true });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/v1/solution-tree/flat")
      .then(({ data }) => {
        if (!mounted) return;
        setTree(data.items || data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return tree.filter((n) => {
      if (n.id === targetNode.id) return false;
      // v89.1 · Ocultar nodos sin fisc_matrix_id si "Receta" está marcado
      if (parts.recipe && !n.fisc_matrix_id) return false;
      if (!ql) return true;
      const path = (n.path || []).join(" ").toLowerCase();
      return path.includes(ql) || (n.name || "").toLowerCase().includes(ql);
    }).slice(0, 60);
  }, [tree, q, targetNode.id, parts.recipe]);

  const doClone = async () => {
    if (!sourceId) return;
    const partsArr = Object.entries(parts).filter(([, v]) => v).map(([k]) => k);
    if (partsArr.length === 0) {
      toast.error("Selecciona al menos una parte a duplicar");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/v1/solution-tree/${targetNode.id}/clone-config`, {
        from_node_id: sourceId,
        parts: partsArr,
      });
      onCloned(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al duplicar");
    } finally {
      setSubmitting(false);
    }
  };

  const selected = tree.find((n) => n.id === sourceId);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="clone-config-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-yellow-50">
          <div>
            <h3 className="text-sm font-bold text-[#0A3747] flex items-center gap-2">
              <Copy size={16} /> Duplicar configuración
            </h3>
            <p className="text-[11px] text-gray-600 mt-0.5">
              Copiar Receta / SLA / Formulario desde otro nodo hacia{" "}
              <strong className="text-[#0A3747]">{targetNode.name || targetNode.activity_name || targetNode.code}</strong>
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-yellow-100 rounded" data-testid="clone-close">
            <X size={16} />
          </button>
        </div>

        {/* Origen */}
        <div className="px-5 pt-4">
          <label className="block text-[10px] font-mono font-bold uppercase tracking-widest text-gray-500 mb-1.5">
            1 · Buscar nodo origen
          </label>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ej: 'Fibra óptica · Instalación' o 'FTTH residencial'…"
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-[#0A3747] focus:outline-none"
            data-testid="clone-search"
          />
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="text-center py-6 text-xs text-gray-500 flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={14} /> Cargando árbol…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-6 text-xs text-gray-500">
              No hay coincidencias {q ? `para "${q}"` : ""}
            </div>
          ) : (
            <ul className="space-y-1">
              {filtered.map((n) => (
                <li key={n.id}>
                  <label
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${
                      sourceId === n.id
                        ? "border-[#0A3747] bg-[#0A3747]/5"
                        : "border-transparent hover:bg-gray-50"
                    }`}
                    data-testid={`clone-src-${n.id}`}
                  >
                    <input
                      type="radio"
                      name="clone-source"
                      checked={sourceId === n.id}
                      onChange={() => setSourceId(n.id)}
                      className="w-4 h-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0A3747] truncate">{n.name}</div>
                      <div className="text-[10px] text-gray-500 truncate">
                        {(n.path || []).join(" › ")}
                        {n.has_rules && <span className="ml-2 text-amber-600 font-bold">● REGLAS</span>}
                      </div>
                    </div>
                    <span className="text-[10px] text-gray-400 font-mono">L{n.depth}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Partes */}
        <div className="px-5 pb-3 border-t border-gray-200 pt-3">
          <label className="block text-[10px] font-mono font-bold uppercase tracking-widest text-gray-500 mb-2">
            2 · ¿Qué copiar?
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: "rules", l: "SLA + Reglas", hint: "SLAs, tecnología, aprovisionamiento, mapeo Odoo y flags" },
              { k: "recipe", l: "Receta", hint: "Se duplica (copia propia del destino)" },
              { k: "form", l: "Formulario", hint: "Se enlaza al mismo formulario del origen" },
            ].map((p) => (
              <label
                key={p.k}
                className={`flex flex-col gap-1 p-2.5 rounded border cursor-pointer ${
                  parts[p.k] ? "border-[#0A3747] bg-[#0A3747]/5" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={parts[p.k]}
                    onChange={(e) => setParts({ ...parts, [p.k]: e.target.checked })}
                    className="w-4 h-4"
                    data-testid={`clone-part-${p.k}`}
                  />
                  <span className="text-xs font-bold text-[#0A3747]">{p.l}</span>
                </div>
                <span className="text-[10px] text-gray-500 ml-6">{p.hint}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={doClone}
            disabled={!sourceId || submitting}
            className="flex-1 px-4 py-2 bg-[#0A3747] text-white text-sm font-bold rounded hover:bg-[#135166] disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="clone-submit"
          >
            {submitting ? <Loader2 className="animate-spin" size={14} /> : <Copy size={14} />}
            {selected ? `Copiar desde "${selected.name}"` : "Selecciona un origen"}
          </button>
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}


/* ═════════════════════════════════════════════════════════════════════
 * ApplyToDescendantsModal · v4.1.0
 * Batch: aplica la config del nodo actual (Reglas/Receta/Formulario) a
 * TODOS sus descendientes activos. Llama a:
 *   POST /v1/solution-tree/{node_id}/apply-to-descendants
 * ═════════════════════════════════════════════════════════════════════ */
function ApplyToDescendantsModal({ sourceNode, onClose, onApplied }) {
  const [parts, setParts] = useState({ rules: true, form: true, recipe: true });
  const [overwrite, setOverwrite] = useState(false);
  const [descendantCount, setDescendantCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Preview del count de descendientes
  useEffect(() => {
    let mounted = true;
    api.get("/v1/solution-tree/flat")
      .then(({ data }) => {
        if (!mounted) return;
        const all = data.items || data || [];
        const name = sourceNode.name || sourceNode.activity_name || "";
        const count = all.filter(
          (n) => n.id !== sourceNode.id && (n.path || []).includes(name) && n.active !== false
        ).length;
        setDescendantCount(count);
      })
      .catch(() => setDescendantCount(0))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [sourceNode]);

  const doApply = async () => {
    const partsArr = Object.entries(parts).filter(([, v]) => v).map(([k]) => k);
    if (partsArr.length === 0) {
      toast.error("Selecciona al menos una parte a aplicar");
      return;
    }
    if (!window.confirm(
      `¿Aplicar ${partsArr.join(", ")} a ${descendantCount ?? "?"} descendientes` +
      `${overwrite ? " (SOBRESCRIBIENDO valores existentes)" : " (respetando valores existentes)"}?`
    )) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/v1/solution-tree/${sourceNode.id}/apply-to-descendants`,
        { parts: partsArr, overwrite }
      );
      onApplied(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar batch");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="apply-descendants-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-cyan-50">
          <div>
            <h3 className="text-sm font-bold text-[#0A3747] flex items-center gap-2">
              <GitBranch size={16} /> Aplicar configuración a descendientes
            </h3>
            <p className="text-[11px] text-gray-600 mt-0.5">
              Propaga la config de{" "}
              <strong className="text-[#0A3747]">{sourceNode.name || sourceNode.activity_name || sourceNode.code}</strong>{" "}
              a todos sus hijos, nietos y bisnietos activos.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Preview count */}
          <div className="rounded-lg border-2 border-cyan-300 bg-cyan-50/50 p-3 flex items-center gap-3">
            <div className="text-3xl font-black text-cyan-800">
              {loading ? "…" : descendantCount ?? 0}
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-bold text-cyan-900 uppercase tracking-wider">
                Descendientes activos
              </div>
              <div className="text-[11px] text-cyan-800 mt-0.5">
                Se aplicará la config seleccionada a estos nodos como una copia independiente.
              </div>
            </div>
          </div>

          {/* Partes */}
          <div>
            <label className="block text-[10px] font-mono font-bold uppercase tracking-widest text-gray-500 mb-2">
              1 · ¿Qué copiar?
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "rules", l: "SLA + Reglas" },
                { k: "recipe", l: "Receta (se duplica)" },
                { k: "form", l: "Formulario (se enlaza)" },
              ].map((p) => (
                <label
                  key={p.k}
                  className={`flex items-center gap-2 p-2.5 rounded border cursor-pointer ${
                    parts[p.k] ? "border-[#0A3747] bg-[#0A3747]/5" : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={parts[p.k]}
                    onChange={(e) => setParts({ ...parts, [p.k]: e.target.checked })}
                    className="w-4 h-4"
                    data-testid={`batch-part-${p.k}`}
                  />
                  <span className="text-xs font-bold text-[#0A3747]">{p.l}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Overwrite */}
          <div>
            <label className="block text-[10px] font-mono font-bold uppercase tracking-widest text-gray-500 mb-2">
              2 · Modo
            </label>
            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3 rounded border cursor-pointer ${!overwrite ? "border-green-500 bg-green-50/50" : "border-gray-200"}`}>
                <input
                  type="radio"
                  name="ow-mode"
                  checked={!overwrite}
                  onChange={() => setOverwrite(false)}
                  className="w-4 h-4 mt-0.5"
                  data-testid="batch-mode-safe"
                />
                <div className="flex-1">
                  <div className="text-xs font-bold text-green-900">Respetar valores existentes (seguro)</div>
                  <div className="text-[11px] text-green-800 mt-0.5">
                    Sólo llena los campos vacíos en cada descendiente. Los que ya tienen valor no cambian.
                  </div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded border cursor-pointer ${overwrite ? "border-red-500 bg-red-50/50" : "border-gray-200"}`}>
                <input
                  type="radio"
                  name="ow-mode"
                  checked={overwrite}
                  onChange={() => setOverwrite(true)}
                  className="w-4 h-4 mt-0.5"
                  data-testid="batch-mode-overwrite"
                />
                <div className="flex-1">
                  <div className="text-xs font-bold text-red-900">Sobrescribir todo (destructivo)</div>
                  <div className="text-[11px] text-red-800 mt-0.5">
                    Pisa cualquier valor previo en los descendientes. Sólo usá esto si sabés lo que hacés.
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={doApply}
            disabled={submitting || descendantCount === 0}
            className="flex-1 px-4 py-2 bg-[#0A3747] text-white text-sm font-bold rounded hover:bg-[#135166] disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="batch-submit"
          >
            {submitting ? <Loader2 className="animate-spin" size={14} /> : <GitBranch size={14} />}
            {descendantCount === 0
              ? "Sin descendientes"
              : `Aplicar a ${descendantCount ?? "?"} descendientes`}
          </button>
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}



/* ═══════════════════════════════════════════════════════════════════
 * AutoLinkModal · v7.5.0
 * Match automático de nodos del árbol con activity_type_code de Odoo.
 * Primero preview (dry_run=true), luego aplicar (dry_run=false).
 * ═══════════════════════════════════════════════════════════════════ */
function AutoLinkModal({ onClose, onApplied }) {
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [minConfidence, setMinConfidence] = useState(0.35);
  const [excluded, setExcluded] = useState(new Set());

  const fetchPreview = async (conf) => {
    setLoading(true);
    try {
      const { data } = await api.post("/v1/solution-tree/auto-link", {
        dry_run: true,
        min_confidence: conf,
      });
      setProposals(data.proposals || []);
      setStats(data.stats_by_code || {});
      setSummary(data);
      setExcluded(new Set());
    } catch (e) {
      toast.error("Error obteniendo preview: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPreview(minConfidence); }, []);

  const doApply = async () => {
    const toApply = proposals.filter((p) => !excluded.has(p.node_id));
    if (!toApply.length) return;
    if (!window.confirm(`¿Aplicar ${toApply.length} mapeos automáticos? Esto vinculará los nodos con sus códigos Odoo.`)) return;
    setApplying(true);
    try {
      await api.post("/v1/solution-tree/auto-link", {
        dry_run: false,
        min_confidence: minConfidence,
      });
      onApplied();
    } catch (e) {
      toast.error("Error aplicando auto-link: " + (e?.response?.data?.detail || e.message));
    } finally {
      setApplying(false);
    }
  };

  const toggleExclude = (nodeId) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    });
  };

  const activeCount = proposals.filter((p) => !excluded.has(p.node_id)).length;

  const confColor = (c) =>
    c >= 0.7 ? "text-emerald-700 bg-emerald-50" :
    c >= 0.5 ? "text-amber-700 bg-amber-50" :
    "text-orange-700 bg-orange-50";

  const confLabel = (c) =>
    c >= 0.7 ? "Alto" : c >= 0.5 ? "Medio" : "Bajo";

  // Group proposals by proposed_code
  const grouped = {};
  proposals.forEach((p) => {
    grouped[p.proposed_code] = grouped[p.proposed_code] || [];
    grouped[p.proposed_code].push(p);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="auto-link-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-white">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-purple-600" />
              <h2 className="text-base font-bold text-[#0A3747]" data-testid="auto-link-title">Auto-Link Masivo · Odoo</h2>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Vinculación automática de nodos del árbol con códigos de actividad de Odoo por coincidencia de nombre.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="auto-link-close">
            <X size={16} />
          </button>
        </div>

        {/* Confidence slider */}
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-4">
          <label className="text-[11px] font-bold text-slate-600 whitespace-nowrap">
            Confianza mínima: <span className="font-mono text-purple-700">{Math.round(minConfidence * 100)}%</span>
          </label>
          <input
            type="range"
            min="0.1"
            max="0.9"
            step="0.05"
            value={minConfidence}
            onChange={(e) => setMinConfidence(parseFloat(e.target.value))}
            className="flex-1 accent-purple-600"
            data-testid="auto-link-confidence-slider"
          />
          <button
            onClick={() => fetchPreview(minConfidence)}
            disabled={loading}
            className="px-3 py-1 text-[11px] bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1"
            data-testid="auto-link-refresh"
          >
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            Recalcular
          </button>
        </div>

        {/* Summary bar */}
        {summary && !loading && (
          <div className="px-5 py-2.5 bg-white border-b border-slate-100 flex items-center gap-4 text-[11px]" data-testid="auto-link-summary">
            <span className="text-slate-500">
              Analizados: <strong className="text-[#0A3747]">{summary.total_nodes_analyzed}</strong>
            </span>
            <span className="text-slate-500">
              Propuestas: <strong className="text-purple-700">{proposals.length}</strong>
            </span>
            <span className="text-slate-500">
              Activos: <strong className="text-emerald-700">{activeCount}</strong>
            </span>
            {Object.entries(stats).map(([code, s]) => (
              <span key={code} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-mono">
                {code}: {s.count}
              </span>
            ))}
          </div>
        )}

        {/* Proposals list */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-purple-400" size={24} />
              <span className="ml-3 text-sm text-slate-500">Analizando {summary?.total_nodes_analyzed || "228"} nodos…</span>
            </div>
          ) : proposals.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-400" data-testid="auto-link-empty">
              No se encontraron coincidencias con confianza ≥ {Math.round(minConfidence * 100)}%.
              Intenta bajar el umbral.
            </div>
          ) : (
            Object.entries(grouped).map(([code, items]) => (
              <div key={code} className="mb-4">
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white/95 backdrop-blur-sm py-1 z-10">
                  <span className="text-[10px] font-mono font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
                    {code}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    {items[0].proposed_name} · {items.length} nodos
                  </span>
                </div>
                <div className="space-y-1">
                  {items.map((p) => {
                    const isExcluded = excluded.has(p.node_id);
                    return (
                      <div
                        key={p.node_id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition ${
                          isExcluded
                            ? "border-slate-200 bg-slate-50 opacity-50"
                            : "border-slate-200 bg-white hover:border-purple-200"
                        }`}
                        data-testid={`auto-link-proposal-${p.node_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!isExcluded}
                          onChange={() => toggleExclude(p.node_id)}
                          className="w-3.5 h-3.5 accent-purple-600"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-[#0A3747] truncate">{p.node_name}</div>
                          <div className="text-[10px] text-slate-400 truncate">
                            {(p.node_path || []).join(" › ")} · L{p.node_depth}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${confColor(p.confidence)}`}>
                            {confLabel(p.confidence)} · {Math.round(p.confidence * 100)}%
                          </span>
                          {p.reasons?.length > 0 && (
                            <span className="text-[9px] text-slate-400 max-w-[140px] truncate" title={p.reasons.join(", ")}>
                              {p.reasons[0]}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={doApply}
            disabled={applying || activeCount === 0}
            className="flex-1 px-4 py-2.5 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 disabled:opacity-40 flex items-center justify-center gap-2 transition"
            data-testid="auto-link-apply"
          >
            {applying ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
            {activeCount === 0
              ? "Sin propuestas"
              : `Aplicar ${activeCount} mapeos`}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 transition"
            data-testid="auto-link-cancel"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══ v99.3.0 · MoveNodeModal ═══
function MoveNodeModal({ node, tree, onClose, onMoved }) {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otsCount, setOtsCount] = useState(null);

  // Flatten tree excluding self + descendants
  const getDescendantIds = (n) => {
    const ids = new Set([n.id]);
    const walk = (children) => {
      (children || []).forEach((c) => { ids.add(c.id); walk(c.children); });
    };
    walk(n.children);
    return ids;
  };
  const excludeIds = getDescendantIds(node);

  const flatCandidates = useMemo(() => {
    const result = [{ id: null, name: "Raíz (nivel superior)", depth: -1 }];
    const walk = (nodes, depth) => {
      (nodes || []).forEach((n) => {
        if (!excludeIds.has(n.id) && n.active !== false) {
          result.push({ id: n.id, name: n.name, depth });
          walk(n.children, depth + 1);
        }
      });
    };
    walk(tree, 0);
    return result;
  }, [tree, node]);

  const doMove = async () => {
    if (selected === undefined) return;
    setLoading(true);
    try {
      const { data } = await api.patch(`/v1/solution-tree/${node.id}`, { parent_id: selected || null });
      setOtsCount(data.open_ots_count);
      onMoved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al mover");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[70vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b bg-blue-50 flex items-center justify-between">
          <span className="font-bold text-sm text-blue-800">Mover "{node.name}" a…</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="overflow-y-auto p-3 space-y-1 flex-1" data-testid="move-tree">
          {flatCandidates.map((c) => (
            <button
              key={c.id || "root"}
              onClick={() => setSelected(c.id)}
              className={`w-full text-left px-3 py-1.5 rounded text-[12px] transition ${
                selected === c.id ? "bg-blue-100 text-blue-800 font-bold" : "hover:bg-gray-50"
              }`}
              style={{ paddingLeft: `${(c.depth + 1) * 16 + 8}px` }}
              data-testid={`move-target-${c.id || "root"}`}
            >
              {c.name}
            </button>
          ))}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-between gap-3">
          {otsCount !== null && (
            <span className="text-xs text-amber-600">{otsCount} OTs abiertas afectadas</span>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} className="px-3 py-1.5 text-xs border rounded hover:bg-gray-50">Cancelar</button>
            <button
              onClick={doMove}
              disabled={loading || selected === undefined}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40"
              data-testid="move-confirm"
            >
              {loading ? "Moviendo…" : "Mover aquí"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
